import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Plus, Edit, Trash2, Search, Eye, Save, X,
  CheckCircle, AlertCircle, BookOpen,
  FileText, Upload, RefreshCw,
  ArrowLeft, Send, List, Grid, UserCheck,
  Building, User, AlertTriangle,
  Video, MapPin as Venue, QrCode, Calendar as CalendarIcon,
  Link, Clock as TimeIcon, Users as PeopleIcon,
  Download, ArrowUpDown, ArrowUp, ArrowDown
} from 'lucide-react';
import locationService from '../../../api/services/locationService';
import trainingService from '../../../api/services/trainingService';
import { API_BASE_URL } from '../../../api/config';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { useNotification } from '../../../components/common/NotificationProvider';
import ConfirmationDialog from '../../../components/common/ConfirmationDialog';
import { formatDate } from '../../../utils/stringUtils';
import Button from '../../../components/common/Button/Button';
import ExportModal from '../../../components/common/ExportModal';
import {
  exportToCSV,
  generateExportFilename,
  downloadFromBackend
} from '../../../utils/exportUtils';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import PaginationControls from '../../../components/common/Pagination/PaginationControls';
import useUrlTableState from '../../../hooks/useUrlTableState';

const USERS_PAGE_SIZE = 20;

const SessionsScheduling = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showWarning, showInfo } = useNotification();

  // Navigation handlers
  const handleGoBack = () => {
    navigate(-1); // Go back to previous page
  };

  // Format duration from minutes to hours/minutes/seconds
  const formatDuration = (minutes) => {
    if (!minutes || minutes === 0) {
      return '0m';
    }
    const totalSeconds = Math.round(minutes * 60);
    const hours = Math.floor(totalSeconds / 3600);
    const mins = Math.floor((totalSeconds % 3600) / 60);
    const secs = totalSeconds % 60;

    const parts = [];
    if (hours > 0) parts.push(`${hours}h`);
    if (mins > 0) parts.push(`${mins}m`);
    if (secs > 0 && hours === 0) parts.push(`${secs}s`);

    return parts.length > 0 ? parts.join(' ') : '0m';
  };

  // Truncate text for filter options
  const truncateText = (text, maxLength = 40) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Format time only (without date)
  const formatTimeOnly = (dateTime) => {
    if (!dateTime) return 'N/A';
    try {
      const date = new Date(dateTime);
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const ampm = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      return `${displayHours}:${displayMinutes} ${ampm}`;
    } catch (error) {
      return 'N/A';
    }
  };

  const [loading, setLoading] = useState(false);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [error, setError] = useState(null);

  const defaultFilters = useMemo(() => ({
    page: 1,
    limit: 10,
    search: '',
    filterMode: '',
    filterStatus: '',
    filterStartDate: '',
    filterEndDate: ''
  }), []);

  const {
    filters: sessionFilters,
    searchTerm: searchQuery,
    setSearchTerm: setSearchQuery,
    updateFilter: updateSessionFilter,
    updatePage: updateSessionPage,
    updateLimit: updateSessionLimit,
    resetFilters: resetSessionFilters,
  } = useUrlTableState(defaultFilters);

  const searchTerm = searchQuery;
  const setSearchTerm = setSearchQuery;

  const selectedMode = sessionFilters.filterMode || '';
  const setSelectedMode = useCallback((mode) => {
    updateSessionFilter({ filterMode: mode });
  }, [updateSessionFilter]);

  const selectedStatus = sessionFilters.filterStatus || '';
  const setSelectedStatus = useCallback((status) => {
    updateSessionFilter({ filterStatus: status });
  }, [updateSessionFilter]);

  const dateRange = useMemo(() => ({
    startDate: sessionFilters.filterStartDate || '',
    endDate: sessionFilters.filterEndDate || ''
  }), [sessionFilters.filterStartDate, sessionFilters.filterEndDate]);

  const setDateRange = useCallback((range) => {
    updateSessionFilter({ filterStartDate: range.startDate || '', filterEndDate: range.endDate || '' });
  }, [updateSessionFilter]);

  const currentPage = sessionFilters.page;
  const itemsPerPage = sessionFilters.limit;

  const setCurrentPage = useCallback((page) => {
    updateSessionPage(page);
  }, [updateSessionPage]);

  const setItemsPerPage = useCallback((limit) => {
    updateSessionLimit(limit);
  }, [updateSessionLimit]);

  const [viewMode, setViewMode] = useState('list');
  const [sortBy, setSortBy] = useState('sessionDate');
  const [sortOrder, setSortOrder] = useState('');
  const [pagination, setPagination] = useState({});
  const [selectedRows, setSelectedRows] = useState(new Map());
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Bulk Delete State
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Frontend list sorting
  const requestSort = (key) => {
    let direction = 'asc';
    if (sortBy === key && sortOrder === 'asc') {
      direction = 'desc';
    }
    setSortBy(key);
    setSortOrder(direction);
  };

  const getSortIcon = (key) => {
    if (sortBy !== key) {
      return <ArrowUpDown className="h-3 w-3 inline-block ml-1 text-gray-400 group-hover:text-gray-600 transition-colors" />;
    }
    return sortOrder === 'asc' ?
      <ArrowUp className="h-3 w-3 inline-block ml-1 text-gray-700" /> :
      <ArrowDown className="h-3 w-3 inline-block ml-1 text-gray-700" />;
  };

  // Load locations for dropdown
  const loadLocations = async () => {
    try {
      console.log('Loading locations for dropdown...');
      const result = await locationService.getLocations({ isActive: 'all', page: 1, limit: 10 });
      if (result.success) {
        const locationsData = result.data?.locations || result.data || [];
        setLocations(locationsData);
        console.log('Locations loaded:', locationsData.length);
      } else {
        console.warn('Failed to load locations:', result.message);
      }
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  // Load courses - only published courses with minimal required fields using optimized dropdown endpoint
  const loadCourses = async () => {
    try {
      console.log('Loading published courses (optimized)...');
      const result = await trainingService.getCoursesForDropdown({ status: 'published', limit: 1000 });
      console.log('Courses result:', result);
      if (result.success) {
        const coursesData = result.data?.courses || [];
        setCourses(coursesData);
        console.log('Published courses loaded (minimal fields):', coursesData.length);
      } else {
        showWarning('Failed to load courses list');
        console.log('Failed to load courses:', result.error);
      }
    } catch (error) {
      console.error('Error loading courses:', error);
      showWarning('Failed to load courses list');
    }
  };

  // Load sessions on component mount and when filters/pagination change
  useEffect(() => {
    loadSessions();
    loadCourses();
    loadLocations();
    // Do NOT reset selectedRows here to allow cross-page selection
  }, [searchTerm, selectedMode, selectedStatus, dateRange, currentPage, itemsPerPage, sortBy, sortOrder]);

  // Load sessions from API
  const loadSessions = async () => {
    setSessionsLoading(true);
    setError(null);

    try {
      const queryParams = {
        search: searchTerm,
        mode: selectedMode,
        status: selectedStatus,
        startDate: dateRange.startDate || undefined,
        endDate: dateRange.endDate || undefined,
        page: currentPage || 1,
        limit: itemsPerPage || 10,
        sortBy: sortBy || undefined,
        sortOrder: sortOrder || undefined
      };

      const result = await trainingService.getSessions(queryParams);

      if (result.success) {
        // Handle the correct data structure from API and normalize mode for UI
        const sessionsData = result.data?.sessions || result.data || [];
        const normalized = (Array.isArray(sessionsData) ? sessionsData : []).map(s => ({
          ...s,
          id: s._id || s.id,
          // derive UI mode from backend type if mode is missing
          mode: s.mode || (s.type === 'classroom' ? 'in-person' : s.type),
          // normalize trainers for UI (backend uses `instructor`)
          trainers: Array.isArray(s.instructor) ? s.instructor : (Array.isArray(s.trainers) ? s.trainers : []),
          // Compute status dynamically based on current time
          status: (() => {
            if (['cancelled', 'postponed'].includes(s.status?.toLowerCase())) return s.status;

            const now = new Date();
            const start = s.startTime ? new Date(s.startTime) : null;
            const end = s.endTime ? new Date(s.endTime) : null;

            if (start && end) {
              if (now < start) return s.status || 'scheduled';
              if (now >= start && now < end) return 'in_progress';
              if (now >= end) return 'completed';
            }
            return s.status;
          })(),
        }));
        setSessions(normalized);

        // Extract pagination data from response
        const paginationData = result.data?.pagination || {};

        // Validate pagination object and sync currentPage
        if (typeof paginationData === 'object' && paginationData !== null) {
          setPagination(paginationData);
          // Sync currentPage state with pagination object
          if (paginationData.currentPage && paginationData.currentPage !== currentPage) {
            setCurrentPage(paginationData.currentPage);
          }
        } else {
          console.warn('Pagination data is not an object:', paginationData);
          setPagination({});
        }
      } else {
        const errorMessage = result.error || 'Failed to load sessions';
        setError(errorMessage);
        setSessions([]);
        setPagination({});
        showError(errorMessage);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
      const errorMessage = 'Failed to load sessions';
      setError(errorMessage);
      setSessions([]);
      setPagination({});
      showError(errorMessage);
    } finally {
      setSessionsLoading(false);
    }
  };

  // Sessions State
  const [sessions, setSessions] = useState([]);
  const [showCreateSession, setShowCreateSession] = useState(false);
  const [editingSession, setEditingSession] = useState(null);
  const [viewingSession, setViewingSession] = useState(null);
  const [courses, setCourses] = useState([]);
  const [locations, setLocations] = useState([]);
  // Trainer selection - using checkbox pattern like assignments
  const [trainerSearchTerm, setTrainerSearchTerm] = useState('');
  const [debouncedTrainerSearch, setDebouncedTrainerSearch] = useState('');
  const [trainerUsers, setTrainerUsers] = useState([]);
  const [trainerUsersPagination, setTrainerUsersPagination] = useState({ currentPage: 1, hasMore: false });
  const [loadingTrainerUsers, setLoadingTrainerUsers] = useState(false);

  // Participant selection - using checkbox pattern like assignments
  const [participantSearchTerm, setParticipantSearchTerm] = useState('');
  const [debouncedParticipantSearch, setDebouncedParticipantSearch] = useState('');
  const [participantUsers, setParticipantUsers] = useState([]);
  const [participantUsersPagination, setParticipantUsersPagination] = useState({ currentPage: 1, hasMore: false });
  const [loadingParticipantUsers, setLoadingParticipantUsers] = useState(false);
  const [userCache, setUserCache] = useState({});

  // Confirmation Dialog State
  const [confirmationDialog, setConfirmationDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null,
    type: 'warning'
  });

  // Form data for session creation/editing
  const [sessionForm, setSessionForm] = useState({
    title: '',
    courseId: '',
    mode: 'in-person', // in-person, virtual
    sessionDate: '',
    startTime: '',
    endTime: '',
    timezone: 'UTC+5:30',
    venue: '',
    meetingLink: '',
    trainerIds: [],
    invitedParticipantIds: [], // New: invited participants
    agenda: '',
    materials: [], // Array to store uploaded materials
    status: 'draft'
  });
  const [isMaterialsDragActive, setIsMaterialsDragActive] = useState(false);
  const [uploadingMaterials, setUploadingMaterials] = useState(false);
  const [deletingMaterials, setDeletingMaterials] = useState({});

  // Refs for date and time inputs to trigger pickers from icons
  const dateInputRef = useRef(null);
  const startTimeInputRef = useRef(null);
  const endTimeInputRef = useRef(null);
  const rescheduleDateInputRef = useRef(null);
  const rescheduleStartTimeInputRef = useRef(null);
  const rescheduleEndTimeInputRef = useRef(null);

  // Field-level validation errors
  const [formErrors, setFormErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState(new Set());

  // Attendance State
  const [showAttendance, setShowAttendance] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [attendanceList, setAttendanceList] = useState([]);


  const modes = [
    { value: 'in-person', label: 'In-Person', icon: Building },
    { value: 'virtual', label: 'Virtual', icon: Video }
  ];

  const statuses = [
    { value: 'scheduled', label: 'Scheduled', color: 'text-gray-600 bg-gray-100' },
    { value: 'in_progress', label: 'In Progress', color: 'text-yellow-600 bg-yellow-100' },
    { value: 'completed', label: 'Completed', color: 'text-brand bg-blue-100' },
    { value: 'cancelled', label: 'Cancelled', color: 'text-red-600 bg-red-100' },
  ];

  const statusFilterOptions = statuses;

  const normalizeStatusValue = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).trim().toLowerCase().replace(/\s+/g, '-').replace(/_/g, '-');
  };

  const getStatusMeta = (value) => {
    const normalized = normalizeStatusValue(value);
    return statuses.find((status) => normalizeStatusValue(status.value) === normalized) || null;
  };

  const getStatusLabel = (value) => {
    const meta = getStatusMeta(value);
    if (meta) return meta.label;
    if (!value) return 'N/A';
    return String(value)
      .replace(/[_-]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const timezones = [
    'UTC+5:30 (IST)',
    'UTC+0 (GMT)',
    'UTC-5 (EST)',
    'UTC-8 (PST)',
    'UTC+1 (CET)',
    'UTC+9 (JST)'
  ];

  // Fallback to mock data if API fails
  useEffect(() => {
    // Remove mock data loading - use API only
  }, [sessions.length, loading, error]);

  // Utility: Capitalize first non-space character
  const capitalizeFirstLetter = (text) => {
    if (!text || typeof text !== 'string') return '';
    const match = text.match(/^(\s*)(.)/);
    if (!match) return text;
    const [, leading, first] = match;
    return leading + first.toUpperCase() + text.slice(leading.length + 1);
  };

  const handleCreateSession = () => {
    // Clear form for new session
    setSessionForm({
      title: '',
      courseId: '',
      mode: 'in-person',
      sessionDate: '',
      startTime: '',
      endTime: '',
      timezone: 'UTC+5:30',
      venue: '',
      meetingLink: '',
      trainerIds: [],
      invitedParticipantIds: [],
      agenda: '',
      materials: [],
      status: 'draft'
    });
    setUserCache({});
    setFormErrors({});
    setTouchedFields(new Set());
    setTrainerUsers([]);
    setTrainerSearchTerm('');
    setParticipantUsers([]);
    setParticipantSearchTerm('');
    setShowCreateSession(true);
    setEditingSession(null);
    setError(null);
  };

  const handleEditSession = async (session) => {
    console.log('Editing session:', session);
    setEditingSession(session);

    // Format dates for form inputs
    const sessionDate = session.scheduledDate ? new Date(session.scheduledDate).toISOString().split('T')[0] : '';
    const startTime = session.startTime ? new Date(session.startTime).toTimeString().slice(0, 5) : '';
    const endTime = session.endTime ? new Date(session.endTime).toTimeString().slice(0, 5) : '';

    console.log('Formatted dates:', { sessionDate, startTime, endTime });
    console.log('Session course:', session.course);

    let dropdownEmployeesCache = null;
    let dropdownEmployeeById = new Map();
    let employeeIdByUserId = new Map();
    let userIdByEmployeeId = new Map();
    const getDropdownEmployees = async () => {
      if (dropdownEmployeesCache !== null) return dropdownEmployeesCache;
      try {
        const response = await fetch(`${API_BASE_URL}/dropdown-data/employees?limit=1000`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
            'Content-Type': 'application/json'
          }
        });
        const result = await response.json();
        if (result.responseCode === 200 && result.data?.employees) {
          dropdownEmployeesCache = result.data.employees;
        } else {
          dropdownEmployeesCache = [];
        }
      } catch (error) {
        console.error('Error fetching employees for mapping:', error);
        dropdownEmployeesCache = [];
      }

      dropdownEmployeeById = new Map();
      employeeIdByUserId = new Map();
      userIdByEmployeeId = new Map();
      dropdownEmployeesCache.forEach(emp => {
        const empId = String(emp._id || emp.id || '');
        if (empId) {
          dropdownEmployeeById.set(empId, emp);
        }
        if (emp.user) {
          employeeIdByUserId.set(String(emp.user), empId);
          userIdByEmployeeId.set(empId, String(emp.user));
        }
      });

      return dropdownEmployeesCache;
    };

    // Extract invited participants from enrolledParticipants
    // Backend returns employee field as Employee ID (mapped from User ID)
    // We need to map Employee IDs to User IDs to match participantUsers list (which has User IDs)
    await getDropdownEmployees();

    const getEmployeeInfo = ({ employeeId, userId }) => {
      if (employeeId && dropdownEmployeeById.has(employeeId)) {
        return dropdownEmployeeById.get(employeeId);
      }
      if (userId && employeeIdByUserId.has(userId)) {
        const mappedEmpId = employeeIdByUserId.get(userId);
        if (mappedEmpId && dropdownEmployeeById.has(mappedEmpId)) {
          return dropdownEmployeeById.get(mappedEmpId);
        }
      }
      return null;
    };

    let invitedParticipants = [];
    let preloadedParticipantUsers = [];
    const participantDetails = Array.isArray(session.enrolledParticipants)
      ? session.enrolledParticipants
        .filter(p => p && p.employee && (p.status === 'enrolled' || !p.status || p.status === undefined))
        .map(p => {
          const employeeId = typeof p.employee === 'object' && p.employee._id
            ? String(p.employee._id)
            : p.employee
              ? String(p.employee)
              : null;
          const userId = p.employeeUser ? String(p.employeeUser) : null;
          const nameFromSession = p.employeeData?.name ||
            (typeof p.employee === 'object'
              ? `${p.employee.firstName || ''} ${p.employee.lastName || ''}`.trim()
              : '') ||
            '';
          const emailFromSession = p.employeeData?.email ||
            (typeof p.employee === 'object' ? p.employee.email : '') ||
            '';
          const departmentFromSession = p.employeeData?.department || (typeof p.employee === 'object' && (p.employee.department || p.employee.employmentInfo?.department)) || '';
          const roleFromSession = (typeof p.employee === 'object' && (p.employee.role || p.employee.employmentInfo?.position)) || '';

          const employeeInfo = getEmployeeInfo({ employeeId, userId });
          const firstName = employeeInfo?.personalInfo?.firstName || '';
          const lastName = employeeInfo?.personalInfo?.lastName || '';
          const nameFromEmployee = `${firstName} ${lastName}`.trim();
          const emailFromEmployee = employeeInfo?.contactInfo?.personalEmail || '';
          const departmentFromEmployee = employeeInfo?.employmentInfo?.department || '';
          const roleFromEmployee = employeeInfo?.employmentInfo?.position || employeeInfo?.employmentInfo?.employmentType || '';

          return {
            employeeId,
            userId,
            name: nameFromSession || nameFromEmployee || 'Participant',
            email: emailFromSession || emailFromEmployee || 'N/A',
            department: departmentFromSession || departmentFromEmployee || 'N/A',
            role: roleFromSession || roleFromEmployee || 'Participant'
          };
        })
        .filter(detail => detail.employeeId || detail.userId)
      : [];

    if (participantDetails.length > 0) {
      participantDetails.forEach(detail => {
        const mappedUserId = userIdByEmployeeId.get(detail.employeeId || '');
        const finalId = detail.userId || mappedUserId || detail.employeeId;
        if (finalId) {
          const finalIdStr = String(finalId);
          invitedParticipants.push(finalIdStr);
          if (!preloadedParticipantUsers.some(user => String(user.id) === finalIdStr)) {
            preloadedParticipantUsers.push({
              id: finalIdStr,
              name: detail.name,
              department: detail.department,
              email: detail.email,
              role: detail.role,
              __preloaded: true
            });
          }
        }
      });
    }

    // Populate user cache with invited participants
    const initialUserCache = {};

    // Ensure all currently needed users are represented with best available data
    if (preloadedParticipantUsers.length > 0) {
      preloadedParticipantUsers.forEach(user => {
        initialUserCache[user.id] = user;
      });
    }

    // Also try to get trainer details from session.instructor
    if (session.instructor) {
      const instructors = Array.isArray(session.instructor) ? session.instructor : [session.instructor];
      instructors.forEach(inst => {
        const id = String(inst.id || inst._id);
        if (id && !initialUserCache[id]) {
          initialUserCache[id] = {
            id,
            name: inst.name || 'N/A',
            email: inst.email || 'N/A',
            department: inst.department || 'N/A',
            role: inst.role || 'Trainer'
          };
        }
      });
    }
    setUserCache(initialUserCache);

    console.log('Extracted invited participants (User IDs for form):', invitedParticipants);
    console.log('Session enrolledParticipants:', session.enrolledParticipants);

    // Extract trainer IDs - backend stores User IDs in instructor.id, but we need Employee IDs
    // For now, we'll use the User IDs and let the backend handle the mapping
    // The backend update route should handle User IDs if Employee IDs aren't available
    let trainerIds = [];
    if (session.instructor) {
      if (Array.isArray(session.instructor)) {
        trainerIds = session.instructor.map(t => t.id || t._id).filter(Boolean);
      } else if (session.instructor.id) {
        trainerIds = [session.instructor.id];
      }
    }

    // Try to map User IDs to Employee IDs by querying employees
    // Note: This is a workaround - ideally backend should store Employee IDs
    if (trainerIds.length > 0) {
      try {
        if (employeeIdByUserId.size === 0) {
          await getDropdownEmployees();
        }
        trainerIds = trainerIds.map(userId => {
          const empId = employeeIdByUserId.get(String(userId));
          return empId || userId; // Fallback to User ID if Employee ID not found
        });
      } catch (error) {
        console.error('Error mapping trainer IDs:', error);
        // Continue with User IDs as fallback
      }
    }

    const normalizedMaterials = Array.isArray(session.materials)
      ? session.materials.map((material, index) => ({
        ...material,
        id: material.id || material._id || material.filename || material.name || `existing-material-${index}`,
        isExisting: true
      }))
      : [];

    setSessionForm({
      title: session.title || '',
      courseId: session.course?.id || session.course?._id || session.course || '',
      mode: session.type === 'classroom' ? 'in-person' : (session.mode || 'virtual'),
      sessionDate: sessionDate,
      startTime: startTime,
      endTime: endTime,
      timezone: session.timezone || 'UTC+5:30',
      venue: session.location || session.venue || '',
      meetingLink: session.meetingLink || '',
      trainerIds: trainerIds.map(id => String(id)), // Normalize to strings
      invitedParticipantIds: invitedParticipants.map(id => String(id)), // Normalize to strings
      agenda: session.description || session.notes || session.agenda || '',
      materials: normalizedMaterials,
      status: session.status || 'draft'
    });
    setFormErrors({});
    setTouchedFields(new Set());
    setTrainerUsers([]);
    setTrainerSearchTerm('');
    setParticipantUsers(preloadedParticipantUsers);
    setParticipantSearchTerm('');
    setShowCreateSession(true);
  };

  const handleFieldBlur = (fieldName) => {
    setTouchedFields(prev => new Set([...prev, fieldName]));
  };

  const handleSaveSession = async () => {
    console.log('=== handleSaveSession called ===');
    console.log('editingSession:', editingSession);
    console.log('sessionForm:', sessionForm);

    try {
      setLoading(true);
      setError(null);

      // Validate required fields (field-level)
      const errors = {};
      const isUrl = (str) => {
        try { const u = new URL(str); return !!u; } catch { return false; }
      };
      if (!sessionForm.title || sessionForm.title.trim().length === 0) {
        errors.title = 'Title is required';
      } else if (sessionForm.title.trim().length < 3) {
        errors.title = 'Title must be at least 3 characters';
      } else if (sessionForm.title.trim().length > 50) {
        errors.title = 'Title must be at most 50 characters';
      }
      if (!sessionForm.courseId) errors.courseId = 'Course is required';
      if (!sessionForm.sessionDate) {
        errors.sessionDate = 'Session date is required';
      } else {
        // Validate that selected date is not in the past
        const selectedDate = new Date(sessionForm.sessionDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate < today) {
          errors.sessionDate = 'Session date cannot be in the past';
        }
      }
      if (!sessionForm.startTime) {
        errors.startTime = 'Start time is required';
      } else if (sessionForm.sessionDate) {
        // Validate start time is not in the past if date is today
        const selectedDate = new Date(sessionForm.sessionDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        if (selectedDate.getTime() === today.getTime()) {
          const now = new Date();
          const [hours, minutes] = sessionForm.startTime.split(':').map(Number);
          const selectedTime = new Date();
          selectedTime.setHours(hours, minutes, 0, 0);
          if (selectedTime < now) {
            errors.startTime = 'Start time cannot be in the past';
          }
        }
      }

      if (!sessionForm.endTime) {
        errors.endTime = 'End time is required';
      } else if (sessionForm.startTime && sessionForm.endTime <= sessionForm.startTime) {
        errors.endTime = 'End time must be after start time';
      }
      if (sessionForm.mode === 'in-person') {
        if (!sessionForm.venue || sessionForm.venue.trim().length === 0) errors.venue = 'Venue is required for in-person session';
      } else {
        if (!sessionForm.meetingLink) {
          errors.meetingLink = 'Meeting link is required for virtual session';
        } else if (!isUrl(sessionForm.meetingLink)) {
          errors.meetingLink = 'Enter a valid meeting URL';
        }
      }
      if (!sessionForm.trainerIds || sessionForm.trainerIds.length === 0) {
        errors.trainers = 'At least one trainer is required';
      }
      if (!sessionForm.invitedParticipantIds || sessionForm.invitedParticipantIds.length === 0) {
        errors.participants = 'At least one invited participant is required';
      }

      // Mark all relevant fields as touched
      const allFields = ['title', 'courseId', 'sessionDate', 'startTime', 'endTime', 'venue', 'meetingLink', 'agenda', 'trainers', 'participants'];
      setTouchedFields(new Set(allFields));

      setFormErrors(errors);
      if (Object.keys(errors).length > 0) {
        showError('Please fix the highlighted fields');

        // Auto-scroll to first error field
        setTimeout(() => {
          const firstErrorField = Object.keys(errors)[0];
          const errorElement = document.querySelector(`[name="${firstErrorField}"], [data-field="${firstErrorField}"]`);
          if (errorElement) {
            errorElement.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            errorElement.focus();
          }
        }, 100);

        return;
      }

      // Prepare session data for API
      const sessionData = {
        title: sessionForm.title,
        courseId: sessionForm.courseId,
        // Map UI mode to backend type and also send original mode for backend compatibility
        type: sessionForm.mode === 'in-person' ? 'classroom' : 'virtual',
        mode: sessionForm.mode,
        sessionDate: sessionForm.sessionDate,
        startTime: sessionForm.sessionDate && sessionForm.startTime ?
          new Date(`${sessionForm.sessionDate}T${sessionForm.startTime}`).toISOString() :
          sessionForm.startTime,
        endTime: sessionForm.sessionDate && sessionForm.endTime ?
          new Date(`${sessionForm.sessionDate}T${sessionForm.endTime}`).toISOString() :
          sessionForm.endTime,
        timezone: sessionForm.timezone,
        venue: sessionForm.venue,
        meetingLink: sessionForm.meetingLink,
        trainerIds: sessionForm.trainerIds,
        invitedParticipantIds: sessionForm.invitedParticipantIds || [],
        agenda: sessionForm.agenda,
        materials: sessionForm.materials,
        status: (() => {
          // If editing a cancelled session, try to reactivate it based on new dates
          if (editingSession && sessionForm.status === 'cancelled') {
            const now = new Date();
            const start = sessionForm.sessionDate && sessionForm.startTime ? new Date(`${sessionForm.sessionDate}T${sessionForm.startTime}`) : null;
            const end = sessionForm.sessionDate && sessionForm.endTime ? new Date(`${sessionForm.sessionDate}T${sessionForm.endTime}`) : null;

            if (start && start > now) return 'scheduled';
            if (start && end && now >= start && now < end) return 'in_progress';
          }
          return sessionForm.status;
        })()
      };

      console.log('Saving session data:', sessionData);
      console.log('Is editing:', !!editingSession);

      let result;
      if (editingSession) {
        // Update existing session
        console.log('Calling updateSession API with ID:', editingSession.id);
        result = await trainingService.updateSession(editingSession.id, sessionData);
        console.log('Update result:', result);
      } else {
        // Create new session
        console.log('Calling createSession API');
        result = await trainingService.createSession(sessionData);
        console.log('Create result:', result);
      }

      if (result.success) {
        // Reload sessions list
        await loadSessions();
        setShowCreateSession(false);
        setEditingSession(null);

        // Show success notification
        showSuccess(editingSession ? 'Session updated successfully!' : 'Session created successfully!');
        console.log('Session saved successfully');
      } else {
        const errorMessage = result.error || 'Failed to save session';
        setError(errorMessage);
        showError(errorMessage);
        console.log('Save failed:', errorMessage);
      }
    } catch (error) {
      console.error('Error saving session:', error);
      const errorMessage = 'Failed to save session. Please try again.';
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (sessionId) => {
    setConfirmationDialog({
      isOpen: true,
      title: 'Delete Session',
      message: 'Are you sure you want to delete this session? This action cannot be undone.',
      onConfirm: async () => {
        try {
          setLoading(true);
          const result = await trainingService.deleteSession(sessionId);

          if (result.success) {
            await loadSessions();
            showSuccess('Session deleted successfully!');
          } else {
            showError(result.error || 'Failed to delete session');
          }
        } catch (error) {
          console.error('Error deleting session:', error);
          showError('Failed to delete session. Please try again.');
        } finally {
          setLoading(false);
        }
      },
      type: 'danger'
    });
  };

  const handlePublishSession = async (sessionId) => {
    try {
      setLoading(true);
      const result = await trainingService.publishSession(sessionId);

      if (result.success) {
        await loadSessions();
        showSuccess('Session published successfully!');
      } else {
        showError(result.error || 'Failed to publish session');
      }
    } catch (error) {
      console.error('Error publishing session:', error);
      showError('Failed to publish session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelSession = async (sessionId) => {
    setConfirmationDialog({
      isOpen: true,
      title: 'Cancel Session',
      message: 'Are you sure you want to cancel this session? Participants will be notified.',
      onConfirm: async () => {
        try {
          setLoading(true);
          const result = await trainingService.cancelSession(sessionId);

          if (result.success) {
            await loadSessions();
            showSuccess('Session cancelled successfully!');
          } else {
            showError(result.error || 'Failed to cancel session');
          }
        } catch (error) {
          console.error('Error cancelling session:', error);
          showError('Failed to cancel session. Please try again.');
        } finally {
          setLoading(false);
        }
      },
      type: 'warning'
    });
  };

  const [reschedulingSession, setReschedulingSession] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState({
    sessionDate: '',
    startTime: '',
    endTime: '',
    reason: ''
  });

  const handleRescheduleSession = (session) => {
    // Format dates for reschedule form
    const sessionDate = session.scheduledDate ? new Date(session.scheduledDate).toISOString().split('T')[0] : '';
    const startTime = session.startTime ? new Date(session.startTime).toTimeString().slice(0, 5) : '';
    const endTime = session.endTime ? new Date(session.endTime).toTimeString().slice(0, 5) : '';

    setRescheduleForm({
      sessionDate: sessionDate,
      startTime: startTime,
      endTime: endTime,
      reason: ''
    });
    setReschedulingSession(session);
  };

  const handleSaveReschedule = async () => {
    if (!reschedulingSession) return;

    // Validate reschedule form
    const errors = {};
    if (!rescheduleForm.sessionDate) {
      errors.sessionDate = 'Session date is required';
    } else {
      const selectedDate = new Date(rescheduleForm.sessionDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      if (selectedDate < today) {
        errors.sessionDate = 'Session date cannot be in the past';
      }
    }
    if (!rescheduleForm.startTime) {
      errors.startTime = 'Start time is required';
    }
    if (!rescheduleForm.endTime) {
      errors.endTime = 'End time is required';
    } else if (rescheduleForm.startTime && rescheduleForm.endTime <= rescheduleForm.startTime) {
      errors.endTime = 'End time must be after start time';
    }

    if (Object.keys(errors).length > 0) {
      showError('Please fix the validation errors');
      return;
    }

    try {
      setLoading(true);
      const rescheduleData = {
        scheduledDate: rescheduleForm.sessionDate,
        startTime: rescheduleForm.sessionDate && rescheduleForm.startTime ?
          new Date(`${rescheduleForm.sessionDate}T${rescheduleForm.startTime}`).toISOString() :
          rescheduleForm.startTime,
        endTime: rescheduleForm.sessionDate && rescheduleForm.endTime ?
          new Date(`${rescheduleForm.sessionDate}T${rescheduleForm.endTime}`).toISOString() :
          rescheduleForm.endTime,
        reason: rescheduleForm.reason
      };

      const result = await trainingService.rescheduleSession(reschedulingSession.id, rescheduleData);

      if (result.success) {
        await loadSessions();
        setReschedulingSession(null);
        setRescheduleForm({ sessionDate: '', startTime: '', endTime: '', reason: '' });
        showSuccess('Session rescheduled successfully!');
      } else {
        showError(result.error || 'Failed to reschedule session');
      }
    } catch (error) {
      console.error('Error rescheduling session:', error);
      showError('Failed to reschedule session. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const processMaterialFiles = async (fileList) => {
    const files = Array.from(fileList || []);

    if (files.length === 0) {
      return;
    }

    setUploadingMaterials(true);

    try {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', 'application/vnd.ms-powerpoint', 'application/vnd.openxmlformats-officedocument.presentationml.presentation'];

      for (const file of files) {
        if (!allowedTypes.includes(file.type)) {
          showError(`File type not supported: ${file.name}`);
          continue;
        }

        // Validate file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          showError(`File too large: ${file.name}. Maximum size is 10MB.`);
          continue;
        }

        const uploadResult = await trainingService.uploadTrainingFile(file, 'doc');

        if (!uploadResult.success) {
          showError(uploadResult.error || `Failed to upload: ${file.name}`);
          continue;
        }

        const uploaded = uploadResult.data || {};

        // Add uploaded cloud file metadata to materials array
        const fileObject = {
          id: uploaded.id || uploaded.publicId || `uploaded-material-${Date.now()}-${Math.random()}`,
          name: uploaded.name || file.name,
          type: uploaded.type || file.type,
          size: uploaded.size || file.size,
          url: uploaded.url || '',
          publicId: uploaded.publicId || '',
          format: uploaded.format || '',
          uploadedAt: new Date(),
          isExisting: false
        };

        setSessionForm(prev => ({
          ...prev,
          materials: [...prev.materials, fileObject]
        }));

        showSuccess(`File uploaded: ${fileObject.name}`);
      }
    } finally {
      setUploadingMaterials(false);
    }
  };

  // Handle file upload for materials
  const handleFileUpload = async (event) => {
    await processMaterialFiles(event.target.files);

    // Clear the input
    event.target.value = '';
  };

  const handleMaterialsDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsMaterialsDragActive(true);
  };

  const handleMaterialsDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsMaterialsDragActive(false);
    }
  };

  const handleMaterialsDrop = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsMaterialsDragActive(false);

    await processMaterialFiles(event.dataTransfer?.files);
  };

  // Helper: Ensure filename has extension based on MIME type
  const ensureFilenameHasExtension = (filename, mimeType) => {
    if (!filename) return 'file';

    const mimeExtensionMap = {
      'application/pdf': '.pdf',
      'application/msword': '.doc',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
      'application/vnd.ms-excel': '.xls',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
      'application/vnd.ms-powerpoint': '.ppt',
      'application/vnd.openxmlformats-officedocument.presentationml.presentation': '.pptx',
      'image/png': '.png',
      'image/jpeg': '.jpg',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'text/plain': '.txt'
    };

    if (filename.includes('.')) return filename;
    return filename + (mimeExtensionMap[mimeType] || '');
  };

  const handleDownloadMaterial = async (material) => {
    const hasRemoteUrl = Boolean(material?.url);
    const fileUrl = material?.url || (material?.file ? URL.createObjectURL(material.file) : '');

    if (!fileUrl) {
      showError('No file available to download');
      return;
    }

    try {
      // For remote URLs, try to fetch as blob to get MIME type
      if (hasRemoteUrl && (material?.url?.includes('cloudinary.com') || material?.url?.startsWith('http'))) {
        try {
          const response = await fetch(material.url);
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          const finalName = ensureFilenameHasExtension(material?.name || 'material', blob.type);

          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = finalName;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);

          setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
        } catch (fetchError) {
          // Fallback to direct URL download with filename from metadata
          const finalName = ensureFilenameHasExtension(material?.name || 'material', material?.type || '');
          const link = document.createElement('a');
          link.href = fileUrl;
          link.download = finalName;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
      } else {
        // For local files, use direct download with metadata
        const finalName = ensureFilenameHasExtension(material?.name || 'material', material?.type || '');
        const link = document.createElement('a');
        link.href = fileUrl;
        link.download = finalName;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        if (!hasRemoteUrl && material?.file) {
          setTimeout(() => URL.revokeObjectURL(fileUrl), 1000);
        }
      }
    } catch (error) {
      // Ultimate fallback: direct link with best-effort filename
      const finalName = material?.name || 'material';
      const link = document.createElement('a');
      link.href = fileUrl;
      link.download = finalName;
      link.target = '_blank';
      link.rel = 'noopener noreferrer';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  // Remove material from list and delete from Cloudinary
  const handleRemoveMaterial = async (materialId) => {
    setDeletingMaterials(prev => ({ ...prev, [materialId]: true }));
    try {
      const material = sessionForm.materials.find(m => String(m.id) === String(materialId));
      if (material?.publicId) {
        const result = await trainingService.deleteTrainingFile(material.publicId);
        if (!result.success) {
          showError(result.error || 'Failed to delete file from cloud');
          setDeletingMaterials(prev => ({ ...prev, [materialId]: false }));
          return;
        }
      }
      setSessionForm(prev => ({
        ...prev,
        materials: prev.materials.filter(m => String(m.id) !== String(materialId))
      }));
      showSuccess('Material deleted successfully');
    } catch (error) {
      showError('Error deleting material');
    } finally {
      setDeletingMaterials(prev => ({ ...prev, [materialId]: false }));
    }
  };

  const handleManageAttendance = async (session) => {
    try {
      setLoading(true);
      console.log('Loading attendance for session:', session.id);

      // For now, use sample data until backend is ready
      const sampleAttendanceData = {
        session: {
          id: session.id,
          title: session.title,
          sessionId: session.sessionId,
          course: session.course,
          instructor: session.instructor,
          scheduledDate: session.scheduledDate,
          startTime: session.startTime,
          endTime: session.endTime,
          location: session.location,
          maxParticipants: session.maxParticipants
        },
        summary: {
          totalEnrolled: 5,
          present: 3,
          absent: 1,
          late: 1,
          attendancePercentage: 60
        },
        attendanceList: [
          {
            id: 'att1',
            employee: {
              name: 'Alice Johnson',
              email: 'alice.johnson@agrosaf.com',
              department: 'Sales',
              position: 'Sales Manager'
            },
            status: 'present',
            checkInTime: '2025-10-11T09:15:00Z',
            notes: 'On time, participated actively'
          },
          {
            id: 'att2',
            employee: {
              name: 'Bob Smith',
              email: 'bob.smith@agrosaf.com',
              department: 'Marketing',
              position: 'Marketing Specialist'
            },
            status: 'absent',
            checkInTime: null,
            notes: 'Sick leave'
          },
          {
            id: 'att3',
            employee: {
              name: 'Carol Davis',
              email: 'carol.davis@agrosaf.com',
              department: 'HR',
              position: 'HR Manager'
            },
            status: 'late',
            checkInTime: '2025-10-11T09:45:00Z',
            notes: 'Traffic delay'
          },
          {
            id: 'att4',
            employee: {
              name: 'David Wilson',
              email: 'david.wilson@agrosaf.com',
              department: 'Operations',
              position: 'Operations Manager'
            },
            status: 'present',
            checkInTime: '2025-10-11T09:10:00Z',
            notes: 'Early arrival, helped with setup'
          },
          {
            id: 'att5',
            employee: {
              name: 'Emma Brown',
              email: 'emma.brown@agrosaf.com',
              department: 'Finance',
              position: 'Finance Analyst'
            },
            status: 'present',
            checkInTime: '2025-10-11T09:20:00Z',
            notes: 'Good participation'
          }
        ]
      };

      setSelectedSession(sampleAttendanceData.session);
      setAttendanceList(sampleAttendanceData.attendanceList);
      setShowAttendance(true);
      console.log('Sample attendance loaded:', sampleAttendanceData);

      // Try to load from API as well
      try {
        const result = await trainingService.getSessionEnrollments(session.id);
        if (result.success) {
          setSelectedSession(result.data.session);
          setAttendanceList(result.data.attendanceList);
          console.log('Real attendance loaded from API:', result.data);
        }
      } catch (apiError) {
        console.log('API not ready, using sample data:', apiError.message);
      }

    } catch (error) {
      console.error('Error loading attendance:', error);
      showError('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  // Filtering and sorting are now handled by the backend API
  // No client-side filtering needed
  const filteredSessions = Array.isArray(sessions) ? sessions : [];

  const sortedSessions = useMemo(() => {
    let sortableSessions = [...filteredSessions];
    if (sortBy) {
      sortableSessions.sort((a, b) => {
        let aValue = '';
        let bValue = '';

        switch (sortBy) {
          case 'title':
            aValue = (a.title || '').toLowerCase();
            bValue = (b.title || '').toLowerCase();
            break;
          case 'course':
            aValue = (a.course?.title || '').toLowerCase();
            bValue = (b.course?.title || '').toLowerCase();
            break;
          case 'sessionDate':
            aValue = new Date(a.startTime || a.sessionDate || a.scheduledDate || 0).getTime();
            bValue = new Date(b.startTime || b.sessionDate || b.scheduledDate || 0).getTime();
            break;
          case 'mode':
            aValue = (a.mode || a.type || '').toLowerCase();
            bValue = (b.mode || b.type || '').toLowerCase();
            break;
          case 'status':
            aValue = (a.status || '').toLowerCase();
            bValue = (b.status || '').toLowerCase();
            break;
          case 'createdAt':
            aValue = new Date(a.createdAt || 0).getTime();
            bValue = new Date(b.createdAt || 0).getTime();
            break;
          default:
            break;
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return sortableSessions;
  }, [filteredSessions, sortBy, sortOrder]);

  // Pagination handlers
  const handlePageChange = (page) => {
    setCurrentPage(page);
    // Scroll to top of sessions list
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (limit) => {
    setItemsPerPage(limit);
  };

  // Debounce trainer search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedTrainerSearch(trainerSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [trainerSearchTerm]);

  // Debounce participant search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedParticipantSearch(participantSearchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [participantSearchTerm]);

  // Load users for trainers (using same endpoint as assignments)
  const loadTrainerUsers = useCallback(async ({ page = 1, search = '', reset = false } = {}) => {
    setLoadingTrainerUsers(true);
    try {
      const queryString = new URLSearchParams({
        page: page.toString(),
        limit: USERS_PAGE_SIZE.toString()
      });

      if (search) {
        queryString.append('search', search);
      }
      queryString.append('includeSuperAdmin', 'true');

      const response = await fetch(`${API_BASE_URL}/training-assignments/users?${queryString.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        const usersData = result.data?.users || [];

        // Transform the data to match our expected format
        // Normalize IDs to strings for consistent comparison
        const transformedUsers = usersData.map(user => ({
          id: String(user._id),
          name: `${user.firstName} ${user.lastName}`.trim(),
          department: user.department,
          email: user.email,
          role: user.role
        }));

        setTrainerUsers(prev => {
          if (reset || !prev) {
            return transformedUsers;
          }
          const existingIds = new Set(prev.map(user => user.id));
          const merged = transformedUsers.filter(user => !existingIds.has(user.id));
          return [...prev, ...merged];
        });

        // Update user cache
        setUserCache(prev => {
          const next = { ...prev };
          transformedUsers.forEach(user => {
            next[user.id] = user;
          });
          return next;
        });

        const paginationData = result.data?.pagination;
        setTrainerUsersPagination({
          currentPage: paginationData?.currentPage || page,
          hasMore: paginationData?.hasMore || false
        });
      } else {
        if (reset) {
          setTrainerUsers([]);
        }
        setTrainerUsersPagination({
          currentPage: 1,
          hasMore: false
        });
      }
    } catch (error) {
      console.error('Error loading trainer users:', error);
      if (reset) {
        setTrainerUsers([]);
      }
      setTrainerUsersPagination({
        currentPage: 1,
        hasMore: false
      });
    } finally {
      setLoadingTrainerUsers(false);
    }
  }, []);

  // Load users for participants (using same endpoint as assignments)
  const loadParticipantUsers = useCallback(async ({ page = 1, search = '', reset = false } = {}) => {
    setLoadingParticipantUsers(true);
    try {
      const queryString = new URLSearchParams({
        page: page.toString(),
        limit: USERS_PAGE_SIZE.toString()
      });

      if (search) {
        queryString.append('search', search);
      }
      queryString.append('includeSuperAdmin', 'true');

      const response = await fetch(`${API_BASE_URL}/training-assignments/users?${queryString.toString()}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
          'Content-Type': 'application/json'
        }
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        const usersData = result.data?.users || [];

        // Transform the data to match our expected format
        // Normalize IDs to strings for consistent comparison
        const transformedUsers = usersData.map(user => ({
          id: String(user._id),
          name: `${user.firstName} ${user.lastName}`.trim(),
          department: user.department,
          email: user.email,
          role: user.role
        }));

        setParticipantUsers(prev => {
          const base = reset
            ? (prev ? prev.filter(user => user.__preloaded) : [])
            : (prev || []);
          const existingIds = new Set(base.map(user => String(user.id)));
          const merged = transformedUsers.filter(user => !existingIds.has(String(user.id)));
          return [...base, ...merged];
        });

        // Update user cache
        setUserCache(prev => {
          const next = { ...prev };
          transformedUsers.forEach(user => {
            next[user.id] = user;
          });
          return next;
        });

        const paginationData = result.data?.pagination;
        setParticipantUsersPagination({
          currentPage: paginationData?.currentPage || page,
          hasMore: paginationData?.hasMore || false
        });
      } else {
        if (reset) {
          setParticipantUsers([]);
        }
        setParticipantUsersPagination({
          currentPage: 1,
          hasMore: false
        });
      }
    } catch (error) {
      console.error('Error loading participant users:', error);
      if (reset) {
        setParticipantUsers([]);
      }
      setParticipantUsersPagination({
        currentPage: 1,
        hasMore: false
      });
    } finally {
      setLoadingParticipantUsers(false);
    }
  }, []);

  // Load users when form opens or search term changes (trainers)
  useEffect(() => {
    if (showCreateSession) {
      loadTrainerUsers({ page: 1, search: debouncedTrainerSearch.trim(), reset: true });
    }
  }, [showCreateSession, debouncedTrainerSearch, loadTrainerUsers]);

  // Load users when form opens or search term changes (participants)
  useEffect(() => {
    if (showCreateSession) {
      loadParticipantUsers({ page: 1, search: debouncedParticipantSearch.trim(), reset: true });
    }
  }, [showCreateSession, debouncedParticipantSearch, loadParticipantUsers]);

  // Reset users when form closes
  useEffect(() => {
    if (!showCreateSession) {
      setTrainerUsers([]);
      setTrainerSearchTerm('');
      setParticipantUsers([]);
      setParticipantSearchTerm('');
    }
  }, [showCreateSession]);

  // Avoid returning early during session list loading to prevent input focus loss

  const getSessionDateSource = (session) =>
    session.sessionDate || session.startTime || session.endTime || null;

  const formatExportDate = (session) => {
    const source = getSessionDateSource(session);
    if (!source) return '';
    return formatDate(source, { includeTime: false });
  };

  const formatExportTime = (session) => {
    const source = session.startTime || session.sessionDate || session.endTime;
    if (!source) return '';
    try {
      const date = new Date(source);
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      return '';
    }
  };

  const escapeCsvCell = (value) => {
    if (value === null || value === undefined) return '';
    const cell = String(value);
    if (cell.includes(',') || cell.includes('"') || cell.includes('\n')) {
      return `"${cell.replace(/"/g, '""')}"`;
    }
    return cell;
  };

  const handleClearFilters = () => {
    resetSessionFilters();
    setSortBy('sessionDate');
    setSortOrder('');
  };

  // Row selection handlers
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Map(selectedRows);
      sessions?.forEach(session => {
        newSelected.set(session.id || session._id, session);
      });
      setSelectedRows(newSelected);
    } else {
      setSelectedRows(new Map());
    }
  };

  const handleSelectRow = (session) => {
    const newSelected = new Map(selectedRows);
    const id = session.id || session._id;
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.set(id, session);
    }
    setSelectedRows(newSelected);
  };

  // --- Bulk Delete Handlers ---
  const handleBulkDelete = () => {
    if (selectedRows.size === 0) {
      showError('Please select at least one session to delete');
      return;
    }
    setShowBulkDeleteModal(true);
  };

  const confirmBulkDelete = async () => {
    setIsBulkDeleting(true);
    const ids = Array.from(selectedRows.keys());
    let successCount = 0;
    let errorCount = 0;

    for (const id of ids) {
      try {
        const result = await trainingService.deleteSession(id);
        if (result && result.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (err) {
        errorCount++;
      }
    }

    setIsBulkDeleting(false);
    setShowBulkDeleteModal(false);
    setSelectedRows(new Map());
    await loadSessions();

    if (successCount > 0) showSuccess(`${successCount} session(s) deleted successfully`);
    if (errorCount > 0) showError(`Failed to delete ${errorCount} session(s)`);
  };

  const mapSessionsForExport = (sessionsToMap) => {
    return sessionsToMap.map(session => ({
      'Session ID': session.sessionId || 'N/A',
      'Title': session.title || 'Untitled Session',
      'Course': session.course?.title || 'N/A',
      'Type': session.type === 'classroom' ? 'In-person' : 'Virtual',
      'Instructor(s)': (session.instructor || []).map(inst => inst.name || 'N/A').join('; '),
      'Scheduled Date': session.scheduledDate ? new Date(session.scheduledDate).toLocaleDateString('en-IN') : 'N/A',
      'Time': `${formatTimeOnly(session.startTime)} - ${formatTimeOnly(session.endTime)}`,
      'Location': session.location || session.venue || 'N/A',
      'Max Participants': session.maxParticipants || 0,
      'Enrolled Count': session.currentParticipants || (session.enrolledParticipants || []).length,
      'Status': capitalizeFirstLetter(session.status || 'N/A')
    }));
  };

  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      const sessionsToExport = Array.from(selectedRows.values());
      const exportData = mapSessionsForExport(sessionsToExport);
      exportToCSV(exportData, 'sessions', {
        title: 'Sessions Scheduling List',
        type: 'Selected Sessions'
      });
    } else {
      setShowExportModal(true);
    }
  };

  const handleExportCurrentPage = () => {
    const exportData = mapSessionsForExport(sessions);
    exportToCSV(exportData, 'sessions_page', {
      title: 'Sessions Scheduling List',
      type: 'Current Page'
    });
    setShowExportModal(false);
  };

  const handleExportRange = async (startDate, endDate) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('type', `Date Range (${startDate} to ${endDate})`);
      if (startDate) queryParams.append('startDate', startDate);
      if (endDate) queryParams.append('endDate', endDate);
      if (searchTerm) queryParams.append('search', searchTerm);
      if (selectedMode) queryParams.append('mode', selectedMode);
      if (selectedStatus) queryParams.append('status', selectedStatus);

      const url = `${API_BASE_URL}/training-sessions/export?${queryParams.toString()}`;
      const filename = generateExportFilename('sessions_range');
      await downloadFromBackend(url, filename);
      setShowExportModal(false);
      showSuccess('Export started successfully');
    } catch (error) {
      console.error('Export failed:', error);
      showError('An error occurred during export');
    } finally {
      setExportLoading(false);
    }
  };

  const handleExportAll = async () => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('type', 'All Data');
      if (searchTerm) queryParams.append('search', searchTerm);

      const url = `${API_BASE_URL}/training-sessions/export?${queryParams.toString()}`;
      const filename = generateExportFilename('sessions_all');
      await downloadFromBackend(url, filename);
      setShowExportModal(false);
      showSuccess('Export started successfully');
    } catch (error) {
      console.error('Export failed:', error);
      showError('An error occurred during export');
    } finally {
      setExportLoading(false);
    }
  };

  const totalSessionPages = Number(pagination?.totalPages || 0);
  const totalSessionCount = Number(pagination?.totalSessions || 0);
  const shouldShowSessionsPagination =
    !sessionsLoading && totalSessionPages > 1 && totalSessionCount > itemsPerPage;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <PermissionGate permission="training" action="read">
            <button
              onClick={handleGoBack}
              className="flex items-center px-3 py-2 text-gray-600 hover:text-gray-900 hover:bg-blue-100/60 rounded-lg transition-colors"
              title="Go Back"
            >
              <ArrowLeft className="h-5 w-5 mr-2" />
            </button>
          </PermissionGate>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Sessions & Scheduling</h1>
            <p className="text-gray-600 mt-1">Manage training sessions, scheduling, and attendance tracking</p>
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {selectedRows.size > 0 && (
            <PermissionGate permission="training" action="delete">
              <button
                onClick={handleBulkDelete}
                className="flex items-center px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete ({selectedRows.size})
              </button>
            </PermissionGate>
          )}
          <PermissionGate permission="training" action="read">
            <Button
              variant={selectedRows.size > 0 ? "primary" : "outline"}
              icon={<Download className="h-4 w-4" />}
              onClick={handleExportClick}
              loading={exportLoading}
            >
              {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
            </Button>
          </PermissionGate>
          <PermissionGate permission="training" action="create">
            <button
              onClick={handleCreateSession}
              className="btn-create flex items-center"
            >
              <Plus className="h-5 w-5 mr-2" />
              Create Session
            </button>
          </PermissionGate>
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 overflow-visible">
        <div className="flex flex-wrap items-end gap-4 w-full overflow-visible">
          <div className="relative flex-[2] min-w-[200px]">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by title, course..."
              value={searchTerm}
              onChange={(e) => {
                const value = e.target.value.slice(0, 100);
                setSearchTerm(value);
              }}
              maxLength={100}
              className="input-field pl-10 pr-10 p-2"
            />
          </div>

          <div className="flex-shrink-0 relative z-10">
            <select
              value={selectedMode}
              onChange={(e) => {
                setSelectedMode(e.target.value);
              }}
              className="input-field w-40 p-2"
            >
              <option value="">All Modes</option>
              {modes.map(mode => (
                <option key={mode.value} value={mode.value}>{mode.label}</option>
              ))}
            </select>
          </div>
          <div className="flex-shrink-0 relative z-10">
            <select
              value={selectedStatus}
              onChange={(e) => {
                setSelectedStatus(e.target.value);
              }}
              className="input-field w-40 p-2"
            >
              <option value="">All Statuses</option>
              {statusFilterOptions.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>

          <div className="flex-shrink-0 relative z-10 w-auto">
            <DateRangeFilter
              startDate={dateRange.startDate}
              endDate={dateRange.endDate}
              onChange={(range) => {
                setDateRange(range);
              }}
            />
          </div>


          {(searchTerm || selectedMode || selectedStatus || dateRange.startDate || dateRange.endDate || sortBy !== 'sessionDate') && (
            <div className="relative flex-shrink-0">
              <Button
                variant="outline"
                icon={<X className="h-4 w-4" />}
                onClick={handleClearFilters}
              >
                Clear
              </Button>
            </div>
          )}


        </div>
      </div>

      {/* Sessions Table */}
      {!sessionsLoading && sortedSessions.length === 0 ? (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12">
          <div className="flex flex-col items-center justify-center text-center">
            <FileText className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No sessions found</h3>
            <p className="text-gray-500">
              {(searchTerm || selectedMode || selectedStatus || dateRange.startDate || dateRange.endDate || sortBy !== 'sessionDate')
                ? "Try adjusting your search or filter criteria."
                : "Get started by creating your first session."}
            </p>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <colgroup>
                <col style={{ width: '4%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '15%' }} />
                <col style={{ width: '8%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '12%' }} />
              </colgroup>
              <thead className="bg-transparent" style={{ backgroundColor: 'rgba(59, 130, 246, 0.14)' }}>
                <tr>
                  <th className="px-6 py-3 text-left">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-[#0F2942] focus:ring-[#0F2942]/50 h-3 w-3"
                      checked={sessions?.length > 0 && sessions.every(s => selectedRows.has(s.id || s._id))}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer group hover:bg-blue-100/60 transition-colors"
                    onClick={() => requestSort('title')}
                  >
                    <div className="flex items-center">Session Title {getSortIcon('title')}</div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer group hover:bg-blue-100/60 transition-colors"
                    onClick={() => requestSort('course')}
                  >
                    <div className="flex items-center">Course {getSortIcon('course')}</div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer group hover:bg-blue-100/60 transition-colors"
                    onClick={() => requestSort('sessionDate')}
                  >
                    <div className="flex items-center">Date & Time {getSortIcon('sessionDate')}</div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer group hover:bg-blue-100/60 transition-colors"
                    onClick={() => requestSort('mode')}
                  >
                    <div className="flex items-center">Mode {getSortIcon('mode')}</div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer group hover:bg-blue-100/60 transition-colors"
                    onClick={() => requestSort('status')}
                  >
                    <div className="flex items-center">Status {getSortIcon('status')}</div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider cursor-pointer group hover:bg-blue-100/60 transition-colors"
                    onClick={() => requestSort('createdAt')}
                  >
                    <div className="flex items-center">Created At {getSortIcon('createdAt')}</div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-900 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {sortedSessions.map((session) => {
                  const statusMeta = getStatusMeta(session.status);
                  const statusLabel = getStatusLabel(session.status);
                  const normalizedStatusValue = normalizeStatusValue(session.status);

                  return (
                    <tr
                      key={session.id || session._id || session.sessionId}
                      className="hover:bg-blue-50/30"
                      data-status={normalizedStatusValue || undefined}
                    >
                      <td className="px-6 py-4 text-center">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-[#0F2942] focus:ring-[#0F2942]/50 h-3 w-3"
                          checked={selectedRows.has(session.id || session._id)}
                          onChange={() => handleSelectRow(session)}
                        />
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm font-medium text-gray-900 truncate max-w-xs" title={session.title}>
                          {truncateText(session.title, 30)}
                        </div>
                        {session.course?.category && (
                          <div className="mt-1">
                            <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                              {session.course.category}
                            </span>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-sm text-gray-900 truncate max-w-xs" title={session.course?.title || 'N/A'}>
                          {truncateText(session.course?.title || 'N/A', 30)}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-gray-900">
                          <div className="flex items-center">
                            <CalendarIcon className="h-4 w-4 mr-1 text-gray-400" />
                            {session.sessionDate || session.scheduledDate ? formatDate(session.sessionDate || session.scheduledDate, { includeTime: false }) : 'N/A'}
                          </div>
                          <div className="flex items-center mt-1">
                            <TimeIcon className="h-4 w-4 mr-1 text-gray-400" />
                            <span className="text-xs text-gray-600">
                              {formatTimeOnly(session.startTime)} - {formatTimeOnly(session.endTime)}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {session.mode?.toLowerCase() === 'in-person' || session.mode?.toLowerCase() === 'in_person' ? (
                            <>
                              <Venue className="h-4 w-4 mr-1 text-gray-400" />
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                In-Person
                              </span>
                            </>
                          ) : (
                            <>
                              <Video className="h-4 w-4 mr-1 text-gray-400" />
                              <span className="px-2 py-1 text-xs font-semibold rounded-full bg-purple-100 text-purple-800">
                                Virtual
                              </span>
                            </>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-semibold rounded-full ${statusMeta?.color || 'text-gray-600 bg-gray-100'
                            }`}
                        >
                          {statusLabel}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {session.createdAt ? formatDate(session.createdAt, { includeTime: true }) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <PermissionGate permission="training" action="read">
                            <button
                              onClick={() => setViewingSession(session)}
                              className="p-2 text-[#0F2942] hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                              title="View Session Details"
                            >
                              <Eye className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          <PermissionGate permission="training" action="update">
                            <button
                              onClick={() => handleEditSession(session)}
                              className="p-2 text-brand hover:text-blue-800 hover:bg-blue-50/20 rounded-lg transition-colors"
                              title="Edit Session"
                            >
                              <Edit className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                          {normalizedStatusValue === 'draft' && (
                            <PermissionGate permission="training" action="update">
                              <button
                                onClick={() => handlePublishSession(session.id)}
                                className="text-brand hover:text-brand-dark"
                                title="Publish Session"
                              >
                                <Send className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          )}
                          {normalizedStatusValue === 'published' && (
                            <PermissionGate permission="training" action="update">
                              <button
                                onClick={() => handleRescheduleSession(session.id)}
                                className="text-yellow-600 hover:text-yellow-900"
                                title="Reschedule Session"
                              >
                                <RefreshCw className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          )}
                          {normalizedStatusValue !== 'cancelled' && (
                            <PermissionGate permission="training" action="update">
                              <button
                                onClick={() => handleCancelSession(session.id)}
                                className="text-red-600 hover:text-red-900"
                                title="Cancel Session"
                              >
                                <X className="h-4 w-4" />
                              </button>
                            </PermissionGate>
                          )}
                          <PermissionGate permission="training" action="delete">
                            <button
                              onClick={() => handleDeleteSession(session.id)}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                              title="Delete Session"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* Pagination */}
          <PaginationControls
            currentPage={pagination.currentPage || currentPage}
            totalPages={totalSessionPages || 1}
            totalItems={totalSessionCount}
            limit={pagination.limit || itemsPerPage}
            hasNextPage={pagination.hasNextPage !== false && (pagination.currentPage || currentPage) < (totalSessionPages || 1)}
            hasPrevPage={pagination.hasPrevPage !== false && (pagination.currentPage || currentPage) > 1}
            onPageChange={handlePageChange}
            onLimitChange={handleItemsPerPageChange}
            border={false}
          />
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
          <div className="flex items-center">
            <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Create/Edit Session Modal */}
      {showCreateSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[95vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingSession ? 'Edit Session' : 'Create Session'}
              </h2>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Basic Information */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Session Title <span className="text-red-500">*</span></label>
                      <input
                        type="text"
                        name="title"
                        data-field="title"
                        value={sessionForm.title}
                        maxLength={50}
                        onKeyPress={(e) => {
                          // Allow alphanumeric, spaces, and basic punctuation
                          const char = e.key;
                          if (!/^[a-zA-Z0-9\s\-_&().,;:!?'"]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'ArrowLeft' && char !== 'ArrowRight' && char !== 'Tab') {
                            e.preventDefault();
                          }
                        }}
                        onChange={(e) => {
                          let value = e.target.value;
                          // Filter out invalid characters
                          value = value.replace(/[^a-zA-Z0-9\s\-_&().,;:!?'"]/g, '');
                          // Enforce max length
                          if (value.length > 50) {
                            value = value.substring(0, 50);
                          }
                          // Capitalize first letter
                          value = capitalizeFirstLetter(value);
                          setSessionForm(prev => ({ ...prev, title: value }));

                          // Real-time validation
                          if (touchedFields.has('title')) {
                            const t = value.trim();
                            if (t.length === 0) {
                              setFormErrors(prev => ({ ...prev, title: 'Title is required' }));
                            } else if (t.length < 3) {
                              setFormErrors(prev => ({ ...prev, title: 'Title must be at least 3 characters' }));
                            } else {
                              setFormErrors(prev => ({ ...prev, title: null }));
                            }
                          }
                        }}
                        onBlur={() => {
                          handleFieldBlur('title');
                          const t = (sessionForm.title || '').trim();
                          if (t.length === 0) {
                            setFormErrors(prev => ({ ...prev, title: 'Title is required' }));
                          } else if (t.length < 3) {
                            setFormErrors(prev => ({ ...prev, title: 'Title must be at least 3 characters' }));
                          } else if (t.length > 50) {
                            setFormErrors(prev => ({ ...prev, title: 'Title must be at most 50 characters' }));
                          }
                        }}
                        className={`input-field p-2 ${touchedFields.has('title') && formErrors.title ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="Enter session title"
                      />
                      <div className="flex items-center justify-between mt-1">
                        {touchedFields.has('title') && formErrors.title && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {formErrors.title}
                          </p>
                        )}
                        <span className={`text-xs ml-auto ${sessionForm.title.length > 45 ? 'text-red-600' :
                          sessionForm.title.length > 40 ? 'text-yellow-600' :
                            'text-gray-500'
                          }`}>
                          {sessionForm.title.length}/50
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Course <span className="text-red-500">*</span></label>
                      <select
                        name="courseId"
                        data-field="courseId"
                        value={sessionForm.courseId}
                        onChange={(e) => {
                          setSessionForm(prev => ({ ...prev, courseId: e.target.value }));
                          if (touchedFields.has('courseId')) {
                            if (!e.target.value) {
                              setFormErrors(prev => ({ ...prev, courseId: 'Course is required' }));
                            } else {
                              setFormErrors(prev => ({ ...prev, courseId: null }));
                            }
                          }
                        }}
                        onBlur={() => {
                          handleFieldBlur('courseId');
                          if (!sessionForm.courseId) {
                            setFormErrors(prev => ({ ...prev, courseId: 'Course is required' }));
                          }
                        }}
                        className={`input-field p-2 ${touchedFields.has('courseId') && formErrors.courseId ? 'border-red-500' : 'border-gray-300'
                          }`}
                      >
                        <option value="">Select Course</option>
                        {courses.map(course => (
                          <option key={course._id || course.id} value={course._id || course.id} title={course.title}>
                            {truncateText(course.title, 30)}
                          </option>
                        ))}
                      </select>
                      {touchedFields.has('courseId') && formErrors.courseId && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {formErrors.courseId}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Session Mode */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Session Mode</h3>
                  <div className="grid grid-cols-2 gap-4">
                    {modes.map(mode => (
                      <PermissionGate permission="training" action="create" key={mode.value}>
                        <button
                          onClick={() => setSessionForm(prev => ({ ...prev, mode: mode.value }))}
                          className={`p-4 border rounded-lg text-left transition-colors ${sessionForm.mode === mode.value
                            ? 'border-brand bg-brand/5 text-brand'
                            : 'border-gray-300 hover:border-gray-400'
                            }`}
                        >
                          <mode.icon className="h-6 w-6 mb-2" />
                          <div className="font-medium">{mode.label}</div>
                        </button>
                      </PermissionGate>
                    ))}
                  </div>
                </div>

                {/* Date & Time */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Date & Time</h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Session Date <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <input
                          type="date"
                          ref={dateInputRef}
                          name="sessionDate"
                          data-field="sessionDate"
                          value={sessionForm.sessionDate}
                          min={new Date().toISOString().split('T')[0]}
                          onClick={(e) => e.target.showPicker?.()}
                          onChange={(e) => {
                            const val = e.target.value;
                            setSessionForm(prev => ({ ...prev, sessionDate: val }));
                            if (touchedFields.has('sessionDate')) {
                              if (!val) {
                                setFormErrors(prev => ({ ...prev, sessionDate: 'Session date is required' }));
                              } else {
                                const selectedDate = new Date(val);
                                const today = new Date();
                                today.setHours(0, 0, 0, 0);
                                if (selectedDate < today) {
                                  setFormErrors(prev => ({ ...prev, sessionDate: 'Session date cannot be in the past' }));
                                } else {
                                  setFormErrors(prev => ({ ...prev, sessionDate: null }));
                                }
                              }
                            }
                          }}
                          onBlur={() => {
                            handleFieldBlur('sessionDate');
                            if (!sessionForm.sessionDate) {
                              setFormErrors(prev => ({ ...prev, sessionDate: 'Session date is required' }));
                            } else {
                              // Validate that selected date is not in the past
                              const selectedDate = new Date(sessionForm.sessionDate);
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              if (selectedDate < today) {
                                setFormErrors(prev => ({ ...prev, sessionDate: 'Session date cannot be in the past' }));
                              }
                            }
                          }}
                          className={`input-field p-2 ${touchedFields.has('sessionDate') && formErrors.sessionDate ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        <div
                          className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            if (dateInputRef.current && typeof dateInputRef.current.showPicker === 'function') {
                              dateInputRef.current.showPicker();
                            }
                          }}
                        >
                          {/* <CalendarIcon className="h-5 w-5 text-gray-400 hover:text-brand transition-colors" /> */}
                        </div>
                      </div>
                      {touchedFields.has('sessionDate') && formErrors.sessionDate && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {formErrors.sessionDate}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Start Time <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <input
                          type="time"
                          ref={startTimeInputRef}
                          name="startTime"
                          data-field="startTime"
                          value={sessionForm.startTime}
                          onClick={(e) => e.target.showPicker?.()}
                          min={(() => {
                            // If date is today, set min to current time
                            if (sessionForm.sessionDate) {
                              const selectedDate = new Date(sessionForm.sessionDate);
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              if (selectedDate.getTime() === today.getTime()) {
                                // Date is today, return current time in HH:mm format
                                const now = new Date();
                                const hours = String(now.getHours()).padStart(2, '0');
                                const minutes = String(now.getMinutes()).padStart(2, '0');
                                return `${hours}:${minutes}`;
                              }
                            }
                            return '';
                          })()}
                          onChange={(e) => {
                            const newStartTime = e.target.value;
                            setSessionForm(prev => ({ ...prev, startTime: newStartTime }));

                            if (touchedFields.has('startTime')) {
                              if (!newStartTime) {
                                setFormErrors(prev => ({ ...prev, startTime: 'Start time is required' }));
                              } else {
                                setFormErrors(prev => ({ ...prev, startTime: null }));
                              }
                            }

                            // If end time exists and is before new start time, clear it and show error
                            if (sessionForm.endTime && newStartTime && sessionForm.endTime <= newStartTime) {
                              setSessionForm(prev => ({ ...prev, endTime: '' }));
                              setFormErrors(prev => ({ ...prev, endTime: 'End time must be after start time' }));
                              handleFieldBlur('endTime');
                            } else if (formErrors.endTime && sessionForm.endTime && newStartTime && sessionForm.endTime > newStartTime) {
                              // Clear end time error if it's now valid
                              setFormErrors(prev => ({ ...prev, endTime: null }));
                            }
                          }}
                          onBlur={() => {
                            handleFieldBlur('startTime');
                            if (!sessionForm.startTime) {
                              setFormErrors(prev => ({ ...prev, startTime: 'Start time is required' }));
                            } else if (sessionForm.sessionDate) {
                              // Validate start time is not in the past if date is today
                              const selectedDate = new Date(sessionForm.sessionDate);
                              const today = new Date();
                              today.setHours(0, 0, 0, 0);
                              if (selectedDate.getTime() === today.getTime()) {
                                const now = new Date();
                                const [hours, minutes] = sessionForm.startTime.split(':').map(Number);
                                const selectedTime = new Date();
                                selectedTime.setHours(hours, minutes, 0, 0);
                                if (selectedTime < now) {
                                  setFormErrors(prev => ({ ...prev, startTime: 'Start time cannot be in the past' }));
                                }
                              }
                            }
                          }}
                          className={`input-field p-2 ${touchedFields.has('startTime') && formErrors.startTime ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        <div
                          className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            if (startTimeInputRef.current && typeof startTimeInputRef.current.showPicker === 'function') {
                              startTimeInputRef.current.showPicker();
                            }
                          }}
                        >
                          {/* <TimeIcon className="h-5 w-5 text-gray-400 hover:text-brand transition-colors" /> */}
                        </div>
                      </div>
                      {touchedFields.has('startTime') && formErrors.startTime && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {formErrors.startTime}
                        </p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">End Time <span className="text-red-500">*</span></label>
                      <div className="relative">
                        <input
                          type="time"
                          ref={endTimeInputRef}
                          name="endTime"
                          data-field="endTime"
                          value={sessionForm.endTime}
                          min={sessionForm.startTime || ''}
                          onClick={(e) => e.target.showPicker?.()}
                          onChange={(e) => {
                            const newEndTime = e.target.value;
                            setSessionForm(prev => ({ ...prev, endTime: newEndTime }));

                            if (touchedFields.has('endTime')) {
                              if (!newEndTime) {
                                setFormErrors(prev => ({ ...prev, endTime: 'End time is required' }));
                              } else if (sessionForm.startTime && newEndTime <= sessionForm.startTime) {
                                setFormErrors(prev => ({ ...prev, endTime: 'End time must be after start time' }));
                              } else {
                                setFormErrors(prev => ({ ...prev, endTime: null }));
                              }
                            }
                          }}
                          onBlur={() => {
                            handleFieldBlur('endTime');
                            if (!sessionForm.endTime) {
                              setFormErrors(prev => ({ ...prev, endTime: 'End time is required' }));
                            } else if (sessionForm.startTime && sessionForm.endTime <= sessionForm.startTime) {
                              setFormErrors(prev => ({ ...prev, endTime: 'End time must be after start time' }));
                            }
                          }}
                          className={`input-field p-2 ${touchedFields.has('endTime') && formErrors.endTime ? 'border-red-500' : 'border-gray-300'
                            }`}
                        />
                        <div
                          className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            if (endTimeInputRef.current && typeof endTimeInputRef.current.showPicker === 'function') {
                              endTimeInputRef.current.showPicker();
                            }
                          }}
                        >
                          {/* <TimeIcon className="h-5 w-5 text-gray-400 hover:text-brand transition-colors" /> */}
                        </div>
                      </div>
                      {touchedFields.has('endTime') && formErrors.endTime && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {formErrors.endTime}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Venue/Meeting Details */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Venue/Meeting Details</h3>
                  {sessionForm.mode === 'in-person' ? (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Venue <span className="text-red-500">*</span></label>
                      <select
                        name="venue"
                        data-field="venue"
                        value={sessionForm.venue}
                        onChange={(e) => {
                          const value = e.target.value;
                          setSessionForm(prev => ({ ...prev, venue: value }));

                          if (touchedFields.has('venue')) {
                            if (!value || value.trim().length === 0) {
                              setFormErrors(prev => ({ ...prev, venue: 'Venue is required for in-person session' }));
                            } else {
                              setFormErrors(prev => ({ ...prev, venue: null }));
                            }
                          }
                        }}
                        onBlur={() => {
                          handleFieldBlur('venue');
                          if (!sessionForm.venue || sessionForm.venue.trim().length === 0) {
                            setFormErrors(prev => ({ ...prev, venue: 'Venue is required for in-person session' }));
                          }
                        }}
                        className={`input-field p-2 ${touchedFields.has('venue') && formErrors.venue ? 'border-red-500' : 'border-gray-300'
                          }`}
                      >
                        <option value="">Select Venue</option>
                        {locations.map(loc => (
                          <option key={loc._id || loc.id} value={loc.name}>
                            {loc.name} {loc.code ? `(${loc.code})` : ''} - {loc.address?.city?.name || loc.address?.city || ''}
                          </option>
                        ))}
                        {/* Allow custom venue if not in list? User didn't specify, but I'll add "Other" if needed. 
                            However, the prompt says "show in dropdown", so I'll just show the fetched locations. */}
                      </select>
                      <div className="flex items-center justify-between mt-1">
                        {touchedFields.has('venue') && formErrors.venue && (
                          <p className="text-sm text-red-600 flex items-center">
                            <AlertCircle className="h-4 w-4 mr-1" />
                            {formErrors.venue}
                          </p>
                        )}
                        <span className={`text-xs ml-auto ${sessionForm.venue.length > 180 ? 'text-red-600' :
                          sessionForm.venue.length > 150 ? 'text-yellow-600' :
                            'text-gray-500'
                          }`}>
                          {sessionForm.venue.length}/200
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Meeting Link <span className="text-red-500">*</span></label>
                      <input
                        type="url"
                        name="meetingLink"
                        data-field="meetingLink"
                        value={sessionForm.meetingLink}
                        onChange={(e) => {
                          setSessionForm(prev => ({ ...prev, meetingLink: e.target.value }));
                          if (touchedFields.has('meetingLink')) {
                            const val = e.target.value;
                            if (!val || val.trim().length === 0) {
                              setFormErrors(prev => ({ ...prev, meetingLink: 'Meeting link is required for virtual session' }));
                            } else {
                              try {
                                new URL(val);
                                setFormErrors(prev => ({ ...prev, meetingLink: null }));
                              } catch {
                                setFormErrors(prev => ({ ...prev, meetingLink: 'Enter a valid meeting URL' }));
                              }
                            }
                          }
                        }}
                        onBlur={() => {
                          handleFieldBlur('meetingLink');
                          const v = sessionForm.meetingLink;
                          if (!v || v.trim().length === 0) {
                            setFormErrors(prev => ({ ...prev, meetingLink: 'Meeting link is required for virtual session' }));
                          } else {
                            try {
                              new URL(v);
                            } catch {
                              setFormErrors(prev => ({ ...prev, meetingLink: 'Enter a valid meeting URL' }));
                            }
                          }
                        }}
                        className={`input-field p-2 ${touchedFields.has('meetingLink') && formErrors.meetingLink ? 'border-red-500' : 'border-gray-300'
                          }`}
                        placeholder="https://teams.microsoft.com/l/meetup-join/..."
                      />
                      {touchedFields.has('meetingLink') && formErrors.meetingLink && (
                        <p className="mt-1 text-sm text-red-600 flex items-center">
                          <AlertCircle className="h-4 w-4 mr-1" />
                          {formErrors.meetingLink}
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Trainers */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium text-gray-900">
                      Trainers <span className="text-red-500">*</span>
                      <span className="ml-2 text-sm text-gray-500">
                        {(sessionForm?.trainerIds?.length || 0)} selected
                      </span>
                    </h3>
                    {trainerUsers && trainerUsers.length > 0 && (
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            // Only select users who are not already participants
                            const availableTrainerIds = trainerUsers
                              .filter(user => !sessionForm.invitedParticipantIds.includes(user.id))
                              .map(user => user.id);
                            setSessionForm(prev => ({
                              ...prev,
                              trainerIds: availableTrainerIds
                            }));
                            if (formErrors.trainers && touchedFields.has('trainers')) {
                              setFormErrors(prev => ({ ...prev, trainers: null }));
                            }
                          }}
                          className="text-xs text-[#0F2942] hover:text-blue-800"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSessionForm(prev => ({ ...prev, trainerIds: [] }));
                            handleFieldBlur('trainers');
                            setFormErrors(prev => ({ ...prev, trainers: 'At least one trainer is required' }));
                          }}
                          className="text-xs text-gray-600 hover:text-gray-800"
                        >
                          Clear All
                        </button>
                      </div>
                    )}
                  </div>
                  <div
                    data-field="trainers"
                    onBlur={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        handleFieldBlur('trainers');
                      }
                    }}
                    className={`max-h-40 overflow-y-auto border rounded-lg p-3 ${touchedFields.has('trainers') && formErrors.trainers ? 'border-red-500' : 'border-gray-300'
                      }`}
                  >
                    <div className="space-y-2">
                      <div className="mb-3 flex space-x-2">
                        <input
                          type="text"
                          placeholder="Search users..."
                          value={trainerSearchTerm}
                          onChange={(e) => {
                            // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                            const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                            setTrainerSearchTerm(value);
                          }}
                          onKeyPress={(e) => {
                            // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                            if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          maxLength={100}
                          className="flex-1 p-2 text-sm border border-gray-300 rounded-lg focus:ring-0 focus:border-brand"
                        />
                        <button
                          type="button"
                          onClick={() => loadTrainerUsers({ page: 1, search: trainerSearchTerm.trim(), reset: true })}
                          disabled={loadingTrainerUsers}
                          className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                        >
                          {loadingTrainerUsers ? '...' : '↻'}
                        </button>
                      </div>
                      {loadingTrainerUsers ? (
                        <div className="text-sm text-gray-500 py-2">Loading users...</div>
                      ) : trainerUsers && trainerUsers.length > 0 ? (
                        <>
                          {trainerUsers.map(user => {
                            const isParticipant = sessionForm.invitedParticipantIds.includes(user.id);
                            return (
                              <label
                                key={user.id}
                                className={`flex items-center space-x-2 p-1 rounded ${isParticipant
                                  ? 'cursor-not-allowed opacity-50'
                                  : 'cursor-pointer hover:bg-blue-50/30'
                                  }`}
                                onClick={(e) => {
                                  if (isParticipant) {
                                    e.preventDefault();
                                    showWarning(`${user.name} is already selected as a participant. Please remove them from participants first.`);
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={sessionForm.trainerIds.includes(user.id)}
                                  disabled={isParticipant}
                                  onChange={(e) => {
                                    if (isParticipant) {
                                      showWarning(`${user.name} is already selected as a participant. Please remove them from participants first.`);
                                      return;
                                    }
                                    if (e.target.checked) {
                                      setSessionForm(prev => ({
                                        ...prev,
                                        trainerIds: [...prev.trainerIds, user.id]
                                      }));
                                      // Clear validation error when trainer is selected
                                      if (formErrors.trainers && touchedFields.has('trainers')) {
                                        setFormErrors(prev => ({ ...prev, trainers: null }));
                                      }
                                    } else {
                                      setSessionForm(prev => ({
                                        ...prev,
                                        trainerIds: prev.trainerIds.filter(id => id !== user.id)
                                      }));
                                      // Show validation error if no trainers left
                                      if (sessionForm.trainerIds.length === 1) {
                                        handleFieldBlur('trainers');
                                        setFormErrors(prev => ({ ...prev, trainers: 'At least one trainer is required' }));
                                      }
                                    }
                                  }}
                                  className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded disabled:cursor-not-allowed"
                                />
                                <div className={`text-sm ${isParticipant ? 'text-gray-400' : 'text-gray-700'}`}>
                                  <div className="font-medium capitalize">{user.name}</div>
                                  <div className="text-xs opacity-70">
                                    {user.email} • {user.department || 'N/A'}
                                  </div>
                                </div>
                              </label>
                            );
                          })}
                          {trainerUsersPagination.hasMore && (
                            <button
                              type="button"
                              onClick={() => loadTrainerUsers({
                                page: (trainerUsersPagination.currentPage || 1) + 1,
                                search: debouncedTrainerSearch,
                                reset: false
                              })}
                              disabled={loadingTrainerUsers}
                              className="mt-2 w-full text-center text-sm text-[#0F2942] hover:text-blue-800"
                            >
                              {loadingTrainerUsers ? 'Loading...' : 'Load more'}
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="text-sm text-gray-500 py-2">
                          {loadingTrainerUsers ? 'Loading users...' : 'No users available'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* List of Selected Trainers */}
                  {sessionForm.trainerIds.length > 0 && (
                    <div className="mt-4 border rounded-lg overflow-hidden bg-gray-50">
                      <div className="px-3 py-2 bg-gray-100 border-b border-gray-200">
                        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Selected Trainers</h4>
                      </div>
                      <div className="max-h-48 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Name</th>
                              <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Email</th>
                              <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Department</th>
                              <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {sessionForm.trainerIds.map(id => {
                              const user = userCache[id];
                              if (!user) return null;
                              return (
                                <tr key={id}>
                                  <td className="px-4 py-2 text-xs font-medium text-gray-900">{user.name}</td>
                                  <td className="px-4 py-2 text-xs text-gray-500">{user.email}</td>
                                  <td className="px-4 py-2 text-xs text-gray-500">{user.department || 'N/A'}</td>
                                  <td className="px-4 py-2 text-xs text-right">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSessionForm(prev => ({
                                          ...prev,
                                          trainerIds: prev.trainerIds.filter(tid => tid !== id)
                                        }));
                                        // Show validation error if no trainers left
                                        if (sessionForm.trainerIds.length === 1) {
                                          handleFieldBlur('trainers');
                                          setFormErrors(prev => ({ ...prev, trainers: 'At least one trainer is required' }));
                                        }
                                      }}
                                      className="text-red-600 hover:text-red-800 font-medium inline-flex items-center space-x-1"
                                      title="Remove Trainer"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                      <span>Remove</span>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {touchedFields.has('trainers') && formErrors.trainers && (
                    <p className="text-sm text-red-600 flex items-center mt-2">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {formErrors.trainers}
                    </p>
                  )}
                </div>

                {/* Invited Participants */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="text-lg font-medium text-gray-900">
                      Invited Participants <span className="text-red-500">*</span>
                      <span className="ml-2 text-sm text-gray-500">
                        {(sessionForm?.invitedParticipantIds?.length || 0)} selected
                      </span>
                    </h3>
                    {participantUsers && participantUsers.length > 0 && (
                      <div className="flex space-x-2">
                        <button
                          type="button"
                          onClick={() => {
                            // Only select users who are not already trainers
                            const availableParticipantIds = participantUsers
                              .filter(user => !sessionForm.trainerIds.includes(user.id))
                              .map(user => user.id);
                            setSessionForm(prev => ({
                              ...prev,
                              invitedParticipantIds: availableParticipantIds
                            }));
                            if (formErrors.participants && touchedFields.has('participants')) {
                              setFormErrors(prev => ({ ...prev, participants: null }));
                            }
                          }}
                          className="text-xs text-[#0F2942] hover:text-blue-800"
                        >
                          Select All
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setSessionForm(prev => ({ ...prev, invitedParticipantIds: [] }));
                          }}
                          className="text-xs text-gray-600 hover:text-gray-800"
                        >
                          Clear All
                        </button>
                      </div>
                    )}
                  </div>
                  <div
                    data-field="participants"
                    onBlur={(e) => {
                      if (!e.currentTarget.contains(e.relatedTarget)) {
                        handleFieldBlur('participants');
                      }
                    }}
                    className={`max-h-40 overflow-y-auto border rounded-lg p-3 ${touchedFields.has('participants') && formErrors.participants ? 'border-red-500' : 'border-gray-300'
                      }`}
                  >
                    <div className="space-y-2">
                      <div className="mb-3 flex space-x-2">
                        <input
                          type="text"
                          placeholder="Search users..."
                          value={participantSearchTerm}
                          onChange={(e) => {
                            // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                            const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                            setParticipantSearchTerm(value);
                          }}
                          onKeyPress={(e) => {
                            // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                            if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                              e.preventDefault();
                            }
                          }}
                          maxLength={100}
                          className="flex-1 p-2 text-sm border border-gray-300 rounded-lg focus:ring-0 focus:border-brand"
                        />
                        <button
                          type="button"
                          onClick={() => loadParticipantUsers({ page: 1, search: participantSearchTerm.trim(), reset: true })}
                          disabled={loadingParticipantUsers}
                          className="px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg disabled:opacity-50"
                        >
                          {loadingParticipantUsers ? '...' : '↻'}
                        </button>
                      </div>
                      {loadingParticipantUsers ? (
                        <div className="text-sm text-gray-500 py-2">Loading users...</div>
                      ) : participantUsers && participantUsers.length > 0 ? (
                        <>
                          {participantUsers.map(user => {
                            const isTrainer = sessionForm.trainerIds.includes(user.id);
                            return (
                              <label
                                key={user.id}
                                className={`flex items-center space-x-2 p-1 rounded ${isTrainer
                                  ? 'cursor-not-allowed opacity-50'
                                  : 'cursor-pointer hover:bg-blue-50/30'
                                  }`}
                                onClick={(e) => {
                                  if (isTrainer) {
                                    e.preventDefault();
                                    showWarning(`${user.name} is already selected as a trainer. Please remove them from trainers first.`);
                                  }
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={sessionForm.invitedParticipantIds.some(id => String(id) === String(user.id))}
                                  disabled={isTrainer}
                                  onChange={(e) => {
                                    if (isTrainer) {
                                      showWarning(`${user.name} is already selected as a trainer. Please remove them from trainers first.`);
                                      return;
                                    }
                                    if (e.target.checked) {
                                      setSessionForm(prev => ({
                                        ...prev,
                                        invitedParticipantIds: [...prev.invitedParticipantIds.filter(id => String(id) !== String(user.id)), user.id]
                                      }));
                                      if (formErrors.participants && touchedFields.has('participants')) {
                                        setFormErrors(prev => ({ ...prev, participants: null }));
                                      }
                                    } else {
                                      setSessionForm(prev => ({
                                        ...prev,
                                        invitedParticipantIds: prev.invitedParticipantIds.filter(id => String(id) !== String(user.id))
                                      }));
                                    }
                                  }}
                                  className="h-4 w-4 text-brand focus:ring-brand border-gray-300 rounded disabled:cursor-not-allowed"
                                />
                                <div className={`text-sm ${isTrainer ? 'text-gray-400' : 'text-gray-700'}`}>
                                  <div className="font-medium capitalize">{user.name}</div>
                                  <div className="text-xs opacity-70">
                                    {user.email} • {user.department || 'N/A'}
                                  </div>
                                  {isTrainer && <div className="text-[10px] text-gray-500 italic mt-0.5">Already selected as trainer</div>}
                                </div>
                              </label>
                            );
                          })}
                          {participantUsersPagination.hasMore && (
                            <button
                              type="button"
                              onClick={() => loadParticipantUsers({
                                page: (participantUsersPagination.currentPage || 1) + 1,
                                search: debouncedParticipantSearch,
                                reset: false
                              })}
                              disabled={loadingParticipantUsers}
                              className="mt-2 w-full text-center text-sm text-[#0F2942] hover:text-blue-800"
                            >
                              {loadingParticipantUsers ? 'Loading...' : 'Load more'}
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="text-sm text-gray-500 py-2">
                          {loadingParticipantUsers ? 'Loading users...' : 'No users available'}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* List of Selected Participants */}
                  {sessionForm.invitedParticipantIds.length > 0 && (
                    <div className="mt-4 border rounded-lg overflow-hidden bg-gray-50">
                      <div className="px-3 py-2 bg-gray-100 border-b border-gray-200">
                        <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Selected Participants</h4>
                      </div>
                      <div className="max-h-60 overflow-y-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>
                              <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Name</th>
                              <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Email</th>
                              <th className="px-4 py-2 text-left text-[10px] font-bold text-gray-500 uppercase">Department</th>
                              <th className="px-4 py-2 text-right text-[10px] font-bold text-gray-500 uppercase">Actions</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-100">
                            {sessionForm.invitedParticipantIds.map(id => {
                              const user = userCache[id];
                              if (!user) return null;
                              return (
                                <tr key={id}>
                                  <td className="px-4 py-2 text-xs font-medium text-gray-900">{user.name}</td>
                                  <td className="px-4 py-2 text-xs text-gray-500">{user.email}</td>
                                  <td className="px-4 py-2 text-xs text-gray-500">{user.department || 'N/A'}</td>
                                  <td className="px-4 py-2 text-xs text-right">
                                    <button
                                      type="button"
                                      onClick={() => {
                                        setSessionForm(prev => ({
                                          ...prev,
                                          invitedParticipantIds: prev.invitedParticipantIds.filter(pid => String(pid) !== String(id))
                                        }));
                                      }}
                                      className="text-red-600 hover:text-red-800 font-medium inline-flex items-center space-x-1"
                                      title="Remove Participant"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                      <span>Remove</span>
                                    </button>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                  {touchedFields.has('participants') && formErrors.participants && (
                    <p className="text-sm text-red-600 flex items-center mt-2">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {formErrors.participants}
                    </p>
                  )}
                </div>

                {/* Agenda */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Agenda</label>
                  <textarea
                    name="agenda"
                    data-field="agenda"
                    value={sessionForm.agenda}
                    maxLength={3000}
                    onKeyPress={(e) => {
                      // Allow alphanumeric, spaces, and basic punctuation for text areas
                      const char = e.key;
                      if (!/^[a-zA-Z0-9\s\-_&().,;:!?'"]$/.test(char) && char !== 'Backspace' && char !== 'Delete' && char !== 'ArrowLeft' && char !== 'ArrowRight' && char !== 'Tab' && char !== 'Enter') {
                        e.preventDefault();
                      }
                    }}
                    onChange={(e) => {
                      let value = e.target.value;
                      // Filter out invalid characters
                      value = value.replace(/[^a-zA-Z0-9\s\-_&().,;:!?'"]/g, '');
                      // Enforce max length
                      if (value.length > 3000) {
                        value = value.substring(0, 3000);
                      }
                      setSessionForm(prev => ({ ...prev, agenda: value }));
                    }}
                    rows={3}
                    className="input-field p-2 border-gray-300"
                    placeholder="Enter session agenda..."
                  />
                  <div className="flex justify-end mt-1">
                    <span className={`text-xs ${sessionForm.agenda.length > 900 ? 'text-red-600' :
                      sessionForm.agenda.length > 800 ? 'text-yellow-600' :
                        'text-gray-500'
                      }`}>
                      {sessionForm.agenda.length}/3000
                    </span>
                  </div>
                </div>

                {/* Materials Upload */}
                <div>
                  <h3 className="text-lg font-medium text-gray-900 mb-4">Materials</h3>

                  {/* Upload Area */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-6 text-center mb-4 transition-colors ${isMaterialsDragActive ? 'border-brand bg-brand/5' : 'border-gray-300'}`}
                    onDragOver={handleMaterialsDragOver}
                    onDragLeave={handleMaterialsDragLeave}
                    onDrop={handleMaterialsDrop}
                  >
                    {uploadingMaterials ? (
                      <RefreshCw className="h-12 w-12 text-brand mx-auto mb-4 animate-spin" />
                    ) : (
                      <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    )}
                    <input
                      type="file"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                      onChange={handleFileUpload}
                      disabled={uploadingMaterials}
                      className="hidden"
                      id="materials-upload"
                    />
                    <label
                      htmlFor="materials-upload"
                      className={`btn-create ${uploadingMaterials ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'}`}
                    >
                      {uploadingMaterials ? 'Uploading...' : 'Upload Materials'}
                    </label>
                    <p className="text-sm text-gray-500 mt-2">PDF, DOC, XLS, PPT files supported (Max 10MB each)</p>
                    <p className="text-xs text-gray-500 mt-1">{uploadingMaterials ? 'Uploading to cloud storage...' : 'Or drag and drop files here'}</p>
                  </div>

                  {/* Materials List */}
                  {sessionForm.materials.length > 0 && (
                    <div className="space-y-2">
                      <h4 className="text-sm font-medium text-gray-700">Uploaded Materials:</h4>
                      {sessionForm.materials.map((material) => (
                        <div key={material.id} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <FileText className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{material.name}</p>
                              <p className="text-xs text-gray-500">
                                {(material.size / 1024 / 1024).toFixed(2)} MB
                              </p>
                            </div>
                          </div>
                          <PermissionGate permission="training" action="update">
                            <div className="flex items-center space-x-2">
                              <button
                                type="button"
                                onClick={() => handleDownloadMaterial(material)}
                                className="p-1 text-gray-600 hover:bg-blue-100/60 rounded"
                                title="Download material"
                              >
                                <Download className="h-4 w-4" />
                              </button>
                              <button
                                type="button"
                                onClick={() => handleRemoveMaterial(material.id)}
                                disabled={deletingMaterials[material.id]}
                                className="p-1 text-red-600 hover:bg-red-50 rounded disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Delete material"
                              >
                                {deletingMaterials[material.id] ? (
                                  <RefreshCw className="h-4 w-4 animate-spin" />
                                ) : (
                                  <Trash2 className="h-4 w-4" />
                                )}
                              </button>
                            </div>
                          </PermissionGate>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            {editingSession && (
              <div className="px-6 pt-0">
                <div className="bg-blue-50 border border-blue-100 text-[#0B2033] text-sm rounded-lg px-4 py-3 flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 mt-0.5 text-blue-500" />
                  <p>
                    Updating a live session automatically sends refreshed itineraries to all trainers and invitees, so make sure the details are final before saving.
                  </p>
                </div>
              </div>
            )}
            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3">
              <PermissionGate permission="training" action="read">
                <button
                  onClick={() => {
                    setShowCreateSession(false);
                    setFormErrors({});
                    setTouchedFields(new Set());
                    setTrainerUsers([]);
                    setTrainerSearchTerm('');
                    setParticipantUsers([]);
                    setParticipantSearchTerm('');
                  }}
                  className="btn-cancel"
                >
                  Cancel
                </button>
              </PermissionGate>
              <PermissionGate permission="training" action={editingSession ? "update" : "create"}>
                <button
                  onClick={handleSaveSession}
                  disabled={loading}
                  className="btn-create disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      {editingSession ? 'Updating...' : 'Creating...'}
                    </div>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2 inline" />
                      {editingSession ? 'Update Session' : 'Create Session'}
                    </>
                  )}
                </button>
              </PermissionGate>
            </div>
          </div>
        </div>
      )}

      {/* Attendance Management Modal */}
      {showAttendance && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[95vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900">Manage Attendance - {selectedSession.title}</h2>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Session Info */}
                <div className="bg-gray-50 p-4 rounded-lg">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <div className="text-sm text-gray-600">Course</div>
                      <div className="font-medium">{selectedSession.course?.title || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Date & Time</div>
                      <div className="font-medium">
                        {selectedSession.scheduledDate ? formatDate(selectedSession.scheduledDate, { includeTime: false }) : 'N/A'} -
                        {selectedSession.startTime ? formatDate(selectedSession.startTime, { includeTime: true }) : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm text-gray-600">Location</div>
                      <div className="font-medium">{selectedSession.location || 'N/A'}</div>
                    </div>
                  </div>
                </div>

                {/* Attendance Summary */}
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-[#0F2942]">{selectedSession.summary?.totalEnrolled || 0}</div>
                    <div className="text-sm text-[#0F2942]">Enrolled</div>
                  </div>
                  <div className="bg-blue-50/20 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-brand">{selectedSession.summary?.present || 0}</div>
                    <div className="text-sm text-brand">Present</div>
                  </div>
                  <div className="bg-red-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-red-600">{selectedSession.summary?.absent || 0}</div>
                    <div className="text-sm text-red-600">Absent</div>
                  </div>
                  <div className="bg-yellow-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-yellow-600">{selectedSession.summary?.late || 0}</div>
                    <div className="text-sm text-yellow-600">Late</div>
                  </div>
                  <div className="bg-purple-50 p-4 rounded-lg text-center">
                    <div className="text-2xl font-bold text-purple-600">{selectedSession.summary?.attendancePercentage || 0}%</div>
                    <div className="text-sm text-purple-600">Attendance Rate</div>
                  </div>
                </div>

                {/* QR Code for Check-in */}
                <div className="text-center">
                  <div className="inline-block p-4 bg-white border-2 border-gray-300 rounded-lg">
                    <QrCode className="h-32 w-32 text-gray-400" />
                  </div>
                  <p className="text-sm text-gray-600 mt-2">Scan QR code for quick check-in</p>
                </div>

                {/* Attendance List */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-medium text-gray-900">Attendance List</h3>
                    <div className="text-sm text-gray-600">
                      {attendanceList.length} participants
                    </div>
                  </div>

                  {attendanceList.length > 0 ? (
                    <div className="space-y-2">
                      {attendanceList.map((record, index) => (
                        <div key={record.id || index} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-center space-x-4">
                            <div className="w-10 h-10 bg-gray-300 rounded-full flex items-center justify-center">
                              <User className="h-5 w-5 text-gray-600" />
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">{record.employee?.name || 'Unknown Employee'}</div>
                              <div className="text-sm text-gray-500">{record.employee?.email || 'N/A'}</div>
                              <div className="text-xs text-gray-400">{record.employee?.department || 'N/A'} - {record.employee?.position || 'N/A'}</div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="text-right">
                              <div className={`px-3 py-1 rounded-full text-sm font-medium ${record.status === 'present' ? 'bg-blue-100 text-blue-800' :
                                record.status === 'absent' ? 'bg-red-100 text-red-800' :
                                  record.status === 'late' ? 'bg-yellow-100 text-yellow-800' :
                                    'bg-gray-100 text-gray-800'
                                }`}>
                                {record.status?.charAt(0).toUpperCase() + record.status?.slice(1) || 'Unknown'}
                              </div>
                              {record.checkInTime && (
                                <div className="text-xs text-gray-500 mt-1">
                                  Check-in: {formatDate(record.checkInTime)}
                                </div>
                              )}
                            </div>
                            <div className="flex space-x-1">
                              <button className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs hover:bg-blue-200">
                                Present
                              </button>
                              <button className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs hover:bg-red-200">
                                Absent
                              </button>
                              <button className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs hover:bg-yellow-200">
                                Late
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8 text-gray-500">
                      <User className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No attendance records found for this session.</p>
                      <p className="text-sm">Participants will appear here once they enroll.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowAttendance(false)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-blue-50/30 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => setShowAttendance(false)}
                className="btn-create"
              >
                Save Attendance
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reschedule Session Modal */}
      {reschedulingSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Reschedule Session</h2>
                <button
                  onClick={() => {
                    setReschedulingSession(null);
                    setRescheduleForm({ sessionDate: '', startTime: '', endTime: '', reason: '' });
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-blue-100/60 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="mb-4">
                <p className="text-sm text-gray-600">
                  <strong>Session:</strong> {reschedulingSession.title}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Current Date:</strong> {reschedulingSession.scheduledDate ? formatDate(reschedulingSession.scheduledDate, { includeTime: false }) : 'N/A'}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Current Time:</strong> {reschedulingSession.startTime ? formatTimeOnly(reschedulingSession.startTime) : 'N/A'} - {reschedulingSession.endTime ? formatTimeOnly(reschedulingSession.endTime) : 'N/A'}
                </p>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">New Session Date <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <input
                      type="date"
                      ref={rescheduleDateInputRef}
                      value={rescheduleForm.sessionDate}
                      min={new Date().toISOString().split('T')[0]}
                      onClick={(e) => e.target.showPicker?.()}
                      onChange={(e) => setRescheduleForm(prev => ({ ...prev, sessionDate: e.target.value }))}
                      className="input-field p-2"
                    />
                    <div
                      className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                      onClick={(e) => {
                        e.preventDefault();
                        if (rescheduleDateInputRef.current && typeof rescheduleDateInputRef.current.showPicker === 'function') {
                          rescheduleDateInputRef.current.showPicker();
                        }
                      }}
                    >
                      <CalendarIcon className="h-5 w-5 text-gray-400 hover:text-brand transition-colors" />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">New Start Time <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type="time"
                        ref={rescheduleStartTimeInputRef}
                        value={rescheduleForm.startTime}
                        onClick={(e) => e.target.showPicker?.()}
                        onChange={(e) => setRescheduleForm(prev => ({ ...prev, startTime: e.target.value }))}
                        className="input-field p-2"
                      />
                      <div
                        className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          if (rescheduleStartTimeInputRef.current && typeof rescheduleStartTimeInputRef.current.showPicker === 'function') {
                            rescheduleStartTimeInputRef.current.showPicker();
                          }
                        }}
                      >
                        <TimeIcon className="h-5 w-5 text-gray-400 hover:text-brand transition-colors" />
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">New End Time <span className="text-red-500">*</span></label>
                    <div className="relative">
                      <input
                        type="time"
                        ref={rescheduleEndTimeInputRef}
                        value={rescheduleForm.endTime}
                        onClick={(e) => e.target.showPicker?.()}
                        onChange={(e) => setRescheduleForm(prev => ({ ...prev, endTime: e.target.value }))}
                        className="input-field p-2"
                      />
                      <div
                        className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
                        onClick={(e) => {
                          e.preventDefault();
                          if (rescheduleEndTimeInputRef.current && typeof rescheduleEndTimeInputRef.current.showPicker === 'function') {
                            rescheduleEndTimeInputRef.current.showPicker();
                          }
                        }}
                      >
                        <TimeIcon className="h-5 w-5 text-gray-400 hover:text-brand transition-colors" />
                      </div>
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Reason for Rescheduling</label>
                  <textarea
                    value={rescheduleForm.reason}
                    onChange={(e) => setRescheduleForm(prev => ({ ...prev, reason: e.target.value }))}
                    placeholder="Optional: Provide a reason for rescheduling..."
                    rows={3}
                    className="input-field p-2"
                  />
                </div>
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setReschedulingSession(null);
                  setRescheduleForm({ sessionDate: '', startTime: '', endTime: '', reason: '' });
                }}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-blue-50/30 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveReschedule}
                disabled={loading}
                className="btn-create disabled:opacity-50"
              >
                {loading ? 'Rescheduling...' : 'Reschedule Session'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* View Session Details Modal */}
      {viewingSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[95vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h2 className="text-xl font-semibold text-gray-900">Session Details</h2>
                <button
                  onClick={() => setViewingSession(null)}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-blue-100/60 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            <div className="p-6">
              <div className="space-y-6">
                {/* Session Header */}
                <div className="bg-gradient-to-r from-brand to-brand-dark rounded-lg p-6 text-white">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-2xl font-bold mb-2">{viewingSession.title || 'N/A'}</h3>
                      <div className="flex items-center space-x-3">
                        <span className={`px-3 py-1 text-sm font-semibold rounded-full ${viewingSession.mode === 'in-person' || viewingSession.type === 'classroom' ? 'bg-brand/10 text-brand' : 'bg-purple-100 text-purple-800'
                          }`}>
                          {viewingSession.mode === 'in-person' || viewingSession.type === 'classroom' ? 'In-Person' : 'Virtual'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Course Information */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <BookOpen className="h-5 w-5 mr-2 text-brand" />
                    Course Information
                  </h3>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Course Title</label>
                    <p className="text-base text-gray-900 mt-1">{viewingSession.course?.title || 'N/A'}</p>
                  </div>
                </div>

                {/* Session Details */}
                <div className="bg-white border border-gray-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <CalendarIcon className="h-5 w-5 mr-2 text-brand" />
                    Session Details
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">Scheduled Date</label>
                      <p className="text-base text-gray-900 mt-1">
                        {viewingSession.scheduledDate || viewingSession.sessionDate ? formatDate(viewingSession.scheduledDate || viewingSession.sessionDate, { includeTime: false }) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Start Time</label>
                      <p className="text-base text-gray-900 mt-1">
                        {viewingSession.startTime ? formatTimeOnly(viewingSession.startTime) : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">End Time</label>
                      <p className="text-base text-gray-900 mt-1">
                        {viewingSession.endTime ? formatTimeOnly(viewingSession.endTime) : 'N/A'}
                      </p>
                    </div>
                    {viewingSession.mode === 'in-person' || viewingSession.type === 'classroom' ? (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Venue</label>
                        <p className="text-base text-gray-900 mt-1 break-words">
                          {viewingSession.location || viewingSession.venue || 'N/A'}
                        </p>
                      </div>
                    ) : (
                      viewingSession.meetingLink && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Meeting Link</label>
                          <p className="text-base text-gray-900 mt-1 break-all">
                            <a href={viewingSession.meetingLink} target="_blank" rel="noopener noreferrer" className="text-[#0F2942] hover:underline flex items-center">
                              <Link className="h-4 w-4 mr-1" />
                              {viewingSession.meetingLink}
                            </a>
                          </p>
                        </div>
                      )
                    )}
                  </div>
                </div>

                {/* Trainers */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 max-h-72 overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <UserCheck className="h-5 w-5 mr-2 text-brand" />
                    Trainers ({Array.isArray(viewingSession.instructor) ? viewingSession.instructor.length : Array.isArray(viewingSession.trainers) ? viewingSession.trainers.length : 0})
                  </h3>
                  <div className="space-y-3">
                    {((Array.isArray(viewingSession.instructor) && viewingSession.instructor.length > 0) ||
                      (Array.isArray(viewingSession.trainers) && viewingSession.trainers.length > 0)) ? (
                      (viewingSession.instructor || viewingSession.trainers || []).map((trainer, index) => (
                        <div key={trainer.id || trainer._id || index} className="border border-gray-200 rounded-lg p-4">
                          <div className="flex items-start space-x-3">
                            <div className="w-10 h-10 bg-brand/10 rounded-full flex items-center justify-center">
                              <User className="h-5 w-5 text-brand" />
                            </div>
                            <div className="flex-1">
                              <p className="font-medium text-gray-900">{trainer.name || 'N/A'}</p>
                              <p className="text-sm text-gray-500">{trainer.email || 'N/A'}</p>
                              <p className="text-sm text-gray-500">
                                {trainer.department || 'N/A'}
                              </p>
                              {trainer.expertise && Array.isArray(trainer.expertise) && trainer.expertise.length > 0 && (
                                <div className="mt-2 flex flex-wrap gap-2">
                                  {trainer.expertise.map((exp, expIndex) => (
                                    <span key={expIndex} className="px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded-full">
                                      {exp}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))
                    ) : (
                      <p className="text-gray-500 text-center py-4">No trainers assigned</p>
                    )}
                  </div>
                </div>

                {/* Invited Participants */}
                <div className="bg-white border border-gray-200 rounded-lg p-6 max-h-72 overflow-y-auto">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <PeopleIcon className="h-5 w-5 mr-2 text-brand" />
                    Invited Participants ({Array.isArray(viewingSession.enrolledParticipants) ? viewingSession.enrolledParticipants.length : 0})
                  </h3>
                  <div className="space-y-3">
                    {viewingSession.enrolledParticipants && Array.isArray(viewingSession.enrolledParticipants) && viewingSession.enrolledParticipants.length > 0 ? (
                      viewingSession.enrolledParticipants.map((participant, index) => {
                        // Get participant name and email from employeeData or construct from employee object
                        const participantName = participant.employeeData?.name ||
                          (participant.employee && typeof participant.employee === 'object' && participant.employee.firstName
                            ? `${participant.employee.firstName || ''} ${participant.employee.lastName || ''}`.trim()
                            : 'N/A');
                        const participantEmail = participant.employeeData?.email ||
                          (participant.employee && typeof participant.employee === 'object' && participant.employee.email) ||
                          'N/A';
                        const participantDepartment = participant.employeeData?.department ||
                          (participant.employee && typeof participant.employee === 'object' && participant.employee.department) ||
                          'N/A';
                        const participantStatus = participant.status || 'enrolled';

                        return (
                          <div key={participant.employee || index} className="border border-gray-200 rounded-lg p-4">
                            <div className="flex items-start space-x-3">
                              <div className="w-10 h-10 bg-blue-100 bg-opacity-10 rounded-full flex items-center justify-center">
                                <User className="h-5 w-5 text-[#0F2942]" />
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-gray-900">{participantName}</p>
                                <p className="text-sm text-gray-500">{participantEmail}</p>
                                <p className="text-sm text-gray-500">
                                  {participantDepartment}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })
                    ) : (
                      <p className="text-gray-500 text-center py-4">No invited participants</p>
                    )}
                  </div>
                </div>

                {/* Agenda */}
                {(viewingSession.agenda || viewingSession.description || viewingSession.notes) && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-brand" />
                      Agenda
                    </h3>
                    <p className="text-base text-gray-700 whitespace-pre-wrap break-words">
                      {viewingSession.agenda || viewingSession.description || viewingSession.notes || 'N/A'}
                    </p>
                  </div>
                )}

                {/* Materials */}
                {viewingSession.materials && Array.isArray(viewingSession.materials) && viewingSession.materials.length > 0 && (
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                      <FileText className="h-5 w-5 mr-2 text-brand" />
                      Materials ({viewingSession.materials.length})
                    </h3>
                    <div className="space-y-2">
                      {viewingSession.materials.map((material, index) => (
                        <div key={material.id || index} className="flex items-center justify-between bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center space-x-3">
                            <FileText className="h-5 w-5 text-gray-400" />
                            <div>
                              <p className="text-sm font-medium text-gray-900">{material.name || 'Untitled Material'}</p>
                              {material.size && (
                                <p className="text-xs text-gray-500">
                                  {(material.size / 1024 / 1024).toFixed(2)} MB
                                </p>
                              )}
                            </div>
                          </div>
                          {material.url && (
                            <button
                              type="button"
                              onClick={() => handleDownloadMaterial(material)}
                              className="text-[#0F2942] hover:underline text-sm"
                            >
                              Download
                            </button>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end space-x-3">
              <button
                onClick={() => setViewingSession(null)}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-blue-50/30 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setViewingSession(null);
                  handleEditSession(viewingSession);
                }}
                className="btn-create"
              >
                <Edit className="h-4 w-4 mr-2 inline" />
                Edit Session
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Delete Confirmation Modal */}
      {showBulkDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertTriangle className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">Confirm Bulk Deletion</h3>
            </div>
            <p className="text-gray-600 mb-2">
              Are you sure you want to delete{' '}
              <span className="font-semibold text-red-600">{selectedRows.size}</span>{' '}
              selected session{selectedRows.size !== 1 ? 's' : ''}?
            </p>
            <p className="text-sm text-gray-500 mb-6">This action cannot be undone.</p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={() => setShowBulkDeleteModal(false)}
                disabled={isBulkDeleting}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-blue-50/30 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmBulkDelete}
                disabled={isBulkDeleting}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isBulkDeleting ? (
                  <>
                    <span className="inline-block h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete {selectedRows.size} Session{selectedRows.size !== 1 ? 's' : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={confirmationDialog.isOpen}
        onClose={() => setConfirmationDialog(prev => ({ ...prev, isOpen: false }))}
        onConfirm={confirmationDialog.onConfirm}
        title={confirmationDialog.title}
        message={confirmationDialog.message}
        type={confirmationDialog.type}
      />

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        onExportCurrentPage={handleExportCurrentPage}
        onExportRange={handleExportRange}
        onExportAll={handleExportAll}
        loading={exportLoading}
        title="Export Sessions"
      />
    </div>
  );
};

export default SessionsScheduling;
